import { authHeaders, getBackendUrl } from "../lib/http";
import { useActiveShopId } from "../store/shop.store";
import { apiClient } from "../lib/api";
import { useState, useRef, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FileText,
  Loader2,
  Plus,
  Trash2,
  Tag,
  DollarSign,
  X,
  Save,
  ArrowLeft,
  Image as ImageIcon,
  AlignLeft,
  Package,
  Settings2,
  Layers,
  Truck,
  Type,
  Pencil,
  GripVertical,
  Video,
  Upload,
  RefreshCw,
  Settings,
  Copy,
  ChevronUp,
  ChevronDown,
  Search,
  Check,
  Sparkles,
} from "lucide-react";
import type { PersonalizationQuestion } from "@erc/shared";
import { normalizePersonalization, placeChartImages, etsyPriceFor, etsyNetProfit, listPriceFor, discountedPrice, DEFAULT_DISCOUNT_PCT } from "@erc/shared";
import { PersonalizationEditor } from "../components/listing/PersonalizationEditor";
import { TrendyolTemplateEditor } from "../components/templates/TrendyolTemplateEditor";
import { useShopVertical } from "../lib/useShopVertical";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DEFAULT_DESCRIPTION_FOOTER, DEFAULT_ETSY_SHIPPING_USD } from "../lib/jewelryListing";
import { EtsyPropertiesEditor } from "../components/listing/EtsyPropertiesEditor";
import {
  useTaxonomyProps, templateProps, parseTemplateDefaults, serializeTemplateDefaults,
  JEWELRY_PROPERTY_DEFAULTS, TEMPLATE_DEFAULTS_KEY, type ListingProp,
} from "../lib/etsyProperties";
import { AssetLibrary, AnalysisModal, assetApi, useAssets, type ListingAsset } from "../components/listing/AssetLibrary";
import { ImageSourcePicker } from "../components/listing/ImageSourcePicker";

// ======== Types ========

interface VariationOption {
  label: string;
  priceAdjust?: number;
  sku?: string;
}

interface Variation {
  name: string;
  options: VariationOption[];
}

interface TemplateAttributes {
  /** Etsy hedef net kar % (fiyat bundan hesaplanir) */
  targetMarginPct?: number;
  primaryColor?: string;
  secondaryColor?: string;
  occasion?: string;
  holiday?: string;
  clothingSize?: string;
  sleeveLength?: string;
  neckline?: string;
  clothingStyle?: string;
  condition?: string;
  clothingGraphic?: string;
  sustainability?: string;
  model?: string;
  imageLabels?: string[];
  [key: string]: string | string[] | number | undefined;
}

interface ListingTemplate {
  id: string;
  name: string;
  // Taki tarifi (bkz. TemplateFormData)
  productTypeKey?: string | null;
  conceptCollectionId?: string | null;
  /** Bu sablonla kullanilan MOCKUP gruplari (tekstil/hediyelik) */
  mockupCollectionIds?: string[];
  priceMultiplier?: number | null;
  optionRefs?: OptionRef[] | null;
  description: string;
  tags: string[];
  category: string | null;
  price: number | null;
  sku: string | null;
  brandModel?: string | null;
  whoMade: string;
  whatIsIt: string;
  whenMade: string;
  isSupply: boolean;
  isDigital: boolean;
  autoRenew: boolean;
  isFeatured: boolean;
  shippingProfileId: string | null;
  taxonomyId: number | null;
  quantity: number;
  listingType: string;
  variations: Variation[] | null;
  disabledCombos?: Record<string, string>[] | null;
  personalization: { enabled: boolean; questions?: PersonalizationQuestion[]; instructions?: string; maxLength?: number } | null;
  imageUrls: string[];
  videoUrl: string | null;
  materials: string[];
  attributes: TemplateAttributes | null;
  sectionId: string | null;
  processingProfileId: string | null;
  productionPartnerId: string | null;
  returnPolicyId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ShippingProfile {
  shipping_profile_id: number;
  title: string;
  min_processing_days: number;
  max_processing_days: number;
}

interface ProductionPartner {
  production_partner_id: number;
  partner_name: string;
  location: string;
}

interface ShopSection {
  shop_section_id: number;
  title: string;
  rank: number;
  active_listing_count: number;
}

interface TemplateFormData {
  name: string;
  description: string;
  tags: string[];
  category: string;
  price: string;
  sku: string;
  brandModel: string;
  whoMade: string;
  whatIsIt: string;
  whenMade: string;
  isSupply: boolean;
  isDigital: boolean;
  autoRenew: boolean;
  isFeatured: boolean;
  shippingProfileId: string;
  taxonomyId: string;
  quantity: string;
  listingType: string;
  variations: Variation[];
  disabledCombos: Record<string, string>[]; // Size×Color cells turned off (is_enabled=false)
  personalization: { enabled: boolean; questions: PersonalizationQuestion[] };
  imageUrls: string[];
  imageLabels: string[]; // parallel to imageUrls: "size-chart", "color-chart", "mockup", ""
  videoUrl: string;
  materials: string[];
  attributes: TemplateAttributes;
  sectionId: string;
  processingProfileId: string;
  productionPartnerId: string;
  returnPolicyId: string;
  // Taki tarifi: urun grubu + uretilecek sahne seti + fiyat kurali
  productTypeKey: string;
  conceptCollectionId: string;
  mockupCollectionIds: string[];
  priceMultiplier: string;
  /** Bu sablonda kullanilacak secenek setleri ve rolleri */
  optionRefs: OptionRef[];
}

export interface OptionRef {
  optionSetId: string;
  role: "variation" | "personalization";
  required?: boolean;
}

const EMPTY_FORM: TemplateFormData = {
  name: "", description: "", tags: [], category: "", price: "", sku: "", brandModel: "",
  whoMade: "i_did", whatIsIt: "finished_product", whenMade: "made_to_order",
  isSupply: false, isDigital: false, autoRenew: true, isFeatured: false,
  shippingProfileId: "", taxonomyId: "", quantity: "999", listingType: "physical",
  variations: [],
  disabledCombos: [],
  personalization: { enabled: false, questions: [] },
  imageUrls: [], imageLabels: [], videoUrl: "", materials: [],
  attributes: {},
  sectionId: "", processingProfileId: "made_to_order_1_3", productionPartnerId: "", returnPolicyId: "",
  productTypeKey: "", conceptCollectionId: "", mockupCollectionIds: [], priceMultiplier: "", optionRefs: [],
};

// ======== API ========

const API_BASE = import.meta.env.VITE_API_URL || "";
/** Kayitta gorseller sunucuya tasinip GORECELI yola (/api/files/...) cevriliyor;
 *  sunucu adresi eklenmezse gorsel kirik gorunuyordu. */
const imgSrc = (u?: string | null) =>
  !u ? "" : /^(https?:|data:|blob:)/.test(u) ? u : `${API_BASE}${u}`;

// Aktif magaza basligi (X-Shop-Id) ortak yardimcidan gelir
const getAuthHeaders = authHeaders;

async function fetchTemplates(): Promise<ListingTemplate[]> {
  const res = await fetch(`${API_BASE}/api/listing-templates`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error("Şablonlar yüklenemedi");
  const json = await res.json();
  return json.data ?? json;
}

async function createTemplate(data: Record<string, unknown>): Promise<ListingTemplate> {
  const res = await fetch(`${API_BASE}/api/listing-templates`, {
    method: "POST", headers: getAuthHeaders(), body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Şablon oluşturulamadı");
  const json = await res.json();
  return json.data ?? json;
}

async function updateTemplate(id: string, data: Record<string, unknown>): Promise<ListingTemplate> {
  const res = await fetch(`${API_BASE}/api/listing-templates/${id}`, {
    method: "PATCH", headers: getAuthHeaders(), body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Şablon güncellenemedi");
  const json = await res.json();
  return json.data ?? json;
}

async function duplicateTemplate(id: string): Promise<ListingTemplate> {
  const res = await fetch(`${API_BASE}/api/listing-templates/${id}/duplicate`, {
    method: "POST", headers: getAuthHeaders(), body: "{}",
  });
  if (!res.ok) throw new Error("Şablon kopyalanamadı");
  const json = await res.json();
  return json.data ?? json;
}

async function deleteTemplate(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/listing-templates/${id}`, {
    method: "DELETE", headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error("Şablon silinemedi");
}

// ======== Variation Template API ========

interface VarTemplateOption {
  label: string;
  priceAdjust?: number;
  sellingPrice?: number;
  cost?: number;
  sku?: string;
}

interface VarTemplate {
  id: string;
  userId: string;
  name: string;
  brand?: string | null;
  category: string | null;
  variations: { name: string; options: VarTemplateOption[] }[];
  disabledCombos?: Record<string, string>[] | null;
  syncSource?: { catalogId: string; categories: unknown[] } | null;
  /** Fiyat hesabi ayarlari — sablonla saklanir, duzenleme acilinca geri gelir */
  pricing?: { marginPct?: number; shippingCharge?: number; discountPct?: number; shippingCost?: number } | null;
  createdAt: string;
  updatedAt: string;
}

// Bilinen markalar (varsayilan) + kullanici yeni ekleyebilir (localStorage'da saklanir)
const DEFAULT_BRANDS = ["Printonami", "Comfort Colors", "Bella Canvas", "Gildan"];
// Etsy kurali (Eylul 2026): bir listede en fazla 400 varyasyon kombinasyonu.
// Devre disi ("—") kombinasyonlar da sayilir — Etsy tum kombinasyonlarin
// gonderilmesini zorunlu tutuyor. Eski 400+ listeler Etsy'de duruyor ama
// varyasyonlari artik guncellenemiyor.
const ETSY_MAX_COMBOS = 400;
function comboCount(variations?: Array<{ options: unknown[] }> | null): number {
  if (!variations?.length) return 0;
  return variations.reduce((n, v) => n * v.options.length, 1);
}

function EtsyComboLimitNotice({ variations }: { variations: Array<{ name: string; options: unknown[] }> }) {
  const total = comboCount(variations);
  if (total <= ETSY_MAX_COMBOS) {
    if (total === 0) return null;
    return <p className="text-[10px] text-gray-400">{total}/{ETSY_MAX_COMBOS} kombinasyon — Etsy sınırının içinde.</p>;
  }
  // Oneri: 2 varyasyonda her birinden kac secenek silinirse sinira iner
  const hints = variations.length === 2
    ? variations.map((v, i) => {
        const other = variations[1 - i].options.length;
        const maxHere = Math.floor(ETSY_MAX_COMBOS / Math.max(other, 1));
        return `${v.name}: ${v.options.length - maxHere} seçenek sil (en fazla ${maxHere})`;
      })
    : [];
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700 space-y-1">
      <p className="font-semibold">
        {variations.map((v) => `${v.options.length} ${v.name}`).join(" × ")} = {total} kombinasyon — Etsy en fazla {ETSY_MAX_COMBOS} kabul ediyor.
      </p>
      <p>
        Etsy'nin yeni kuralı: devre dışı ("—") kombinasyonlar da sayılır, çünkü Etsy tüm kombinasyonların gönderilmesini istiyor.
        Kombinasyonları kapatmak sayıyı düşürmez; seçenek silmek gerekir. Bu şablonla oluşturulan listelere varyasyon yüklenemez.
        Daha önce yüklenmiş 400+ listeler Etsy'de durur ama varyasyonları güncellenemez.
      </p>
      {hints.length > 0 && <p className="font-medium">Sınıra inmek için: {hints.join(" veya ")}. Gerekirse şablonu ürün tipine göre ikiye bölün (Kopyala).</p>}
      <p>Kaydetmek için sayıyı {ETSY_MAX_COMBOS}'e indirin.</p>
    </div>
  );
}

function getCustomBrands(): string[] {
  try { return JSON.parse(localStorage.getItem("erc_var_brands") || "[]"); } catch { return []; }
}
function addCustomBrand(brand: string) {
  const b = brand.trim();
  if (!b) return;
  const list = getCustomBrands();
  if (!DEFAULT_BRANDS.includes(b) && !list.includes(b)) {
    list.push(b);
    try { localStorage.setItem("erc_var_brands", JSON.stringify(list)); } catch { /* */ }
  }
}

async function fetchVarTemplates(): Promise<VarTemplate[]> {
  const res = await fetch(`${API_BASE}/api/variation-templates`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error("Varyasyon şablonları yüklenemedi");
  const json = await res.json();
  return json.data ?? json;
}

async function updateVarTemplate(id: string, data: Partial<{ name: string; brand: string | null; category: string; variations: VarTemplate["variations"]; disabledCombos: Record<string, string>[] | null; pricing: VarTemplate["pricing"] }>): Promise<VarTemplate> {
  const res = await fetch(`${API_BASE}/api/variation-templates/${id}`, {
    method: "PATCH", headers: getAuthHeaders(), body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Varyasyon şablonu güncellenemedi");
  const json = await res.json();
  return json.data ?? json;
}

async function deleteVarTemplate(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/variation-templates/${id}`, {
    method: "DELETE", headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error("Varyasyon şablonu silinemedi");
}

async function fetchShippingProfiles(): Promise<ShippingProfile[]> {
  const res = await fetch(`${API_BASE}/api/etsy/listings/shipping-profiles/list`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return [];
  const json = await res.json();
  return json.data?.results ?? [];
}

async function fetchProductionPartners(): Promise<ProductionPartner[]> {
  try {
    const res = await fetch(`${API_BASE}/api/etsy/listings/production-partners/list`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      console.warn("[ProductionPartners] API error:", res.status, await res.text().catch(() => ""));
      return [];
    }
    const json = await res.json();
    console.log("[ProductionPartners] Response:", json);
    // Etsy returns { count, results } - backend wraps as { data: { count, results } }
    const results = json.data?.results ?? json.results ?? [];
    return results;
  } catch (err) {
    console.warn("[ProductionPartners] Fetch error:", err);
    return [];
  }
}

async function fetchShopSections(): Promise<ShopSection[]> {
  try {
    const res = await fetch(`${API_BASE}/api/etsy/listings/shop-sections/list`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return [];
    const json = await res.json();
    return json.data?.results ?? json.results ?? [];
  } catch { return []; }
}

// ======== Etsy Taxonomy, Return Policies, Readiness States ========

interface ReturnPolicy {
  return_policy_id: number;
  accepts_returns: boolean;
  accepts_exchanges: boolean;
}

async function fetchReturnPolicies(): Promise<ReturnPolicy[]> {
  try {
    const res = await fetch(`${API_BASE}/api/etsy/listings/return-policies/list`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return [];
    const json = await res.json();
    return json.data?.results ?? json.results ?? [];
  } catch { return []; }
}

interface ReadinessState {
  readiness_state_id: number;
  readiness_state: string;
  readiness_state_label?: string;
  min_processing_days?: number;
  max_processing_days?: number;
}

async function fetchReadinessStates(): Promise<ReadinessState[]> {
  try {
    const res = await fetch(`${API_BASE}/api/etsy/listings/readiness-states/list`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return [];
    const json = await res.json();
    return json.data?.results ?? json.data?.data?.results ?? json.results ?? [];
  } catch { return []; }
}

interface TaxonomyNode {
  id: number;
  name: string;
  full_path_taxonomy_ids: number[];
  parent_id: number | null;
  children?: TaxonomyNode[];
}

async function fetchTaxonomyNodes(): Promise<TaxonomyNode[]> {
  try {
    const res = await fetch(`${API_BASE}/api/etsy/listings/taxonomy/nodes`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return [];
    const json = await res.json();
    return json.data?.results ?? [];
  } catch { return []; }
}

function flattenTaxonomy(nodes: TaxonomyNode[], parentPath = ""): { id: number; path: string }[] {
  const result: { id: number; path: string }[] = [];
  const seen = new Set<number>();
  function walk(list: TaxonomyNode[], parent: string) {
    for (const node of list) {
      if (seen.has(node.id)) continue;
      seen.add(node.id);
      const path = parent ? `${parent} > ${node.name}` : node.name;
      if (!node.children || node.children.length === 0) {
        result.push({ id: node.id, path });
      } else {
        result.push({ id: node.id, path });
        walk(node.children, path);
      }
    }
  }
  walk(nodes, parentPath);
  return result;
}

// ======== Section definitions ========

type SectionKey = "images" | "video" | "description" | "tags" | "pricing" | "inventory" | "details" | "variations" | "personalization" | "shipping" | "settings";

const SECTIONS: { key: SectionKey; label: string; icon: typeof Type }[] = [
  { key: "images", label: "Görseller", icon: ImageIcon },
  { key: "video", label: "Videolar", icon: Video },
  { key: "description", label: "Açıklamalar", icon: AlignLeft },
  { key: "tags", label: "Etiketler", icon: Tag },
  { key: "pricing", label: "Fiyat", icon: DollarSign },
  { key: "inventory", label: "Envanter", icon: Package },
  { key: "details", label: "Detaylar", icon: Settings2 },
  { key: "variations", label: "Varyasyonlar", icon: Layers },
  { key: "personalization", label: "Kişiselleştirme", icon: Pencil },
  { key: "shipping", label: "Gönderim", icon: Truck },
  { key: "settings", label: "Ayarlar", icon: Settings },
];

function sectionCount(form: TemplateFormData, key: SectionKey): number {
  switch (key) {
    case "images": return form.imageUrls.length;
    case "video": return form.videoUrl ? 1 : 0;
    case "description": return form.description ? 1 : 0;
    case "tags": return form.tags.length;
    case "pricing": return (form.price ? 1 : 0) + (form.quantity && form.quantity !== "999" ? 1 : 0);
    case "inventory": return (form.whoMade !== "i_did" ? 1 : 0) + (form.whenMade !== "made_to_order" ? 1 : 0);
    case "details": return (form.category ? 1 : 0) + (form.taxonomyId ? 1 : 0) + Object.values(form.attributes).filter(Boolean).length;
    case "variations": return form.variations.length;
    case "personalization": return form.personalization.enabled ? 1 : 0;
    case "shipping": return (form.shippingProfileId ? 1 : 0) + (form.processingProfileId ? 1 : 0);
    case "settings": return (form.sectionId ? 1 : 0) + (form.isFeatured ? 1 : 0) + (form.returnPolicyId ? 1 : 0);
  }
}

// ======== Attribute options (OCL-style) ========


// ======== Template Editor ========

/** Rexven sabit ayar denetimi (kargo <= $5.99, iade, uretim ortagi, made to
 *  order, stok, oto-yenileme) — kayitli sablonun Etsy profilleriyle */
function TemplateAuditCard({ templateId }: { templateId: string }) {
  const shopId = useActiveShopId();
  const q = useQuery({
    queryKey: ["template-audit", shopId, templateId],
    queryFn: async () => {
      const r = await fetch(`${getBackendUrl()}/api/listing-templates/${templateId}/audit`, { headers: authHeaders() });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.error || "Denetlenemedi");
      return b.data as Array<{ level: "ok" | "warn"; label: string; detail: string }>;
    },
    staleTime: 60_000,
  });
  if (q.isLoading) return null;
  if (q.isError) return <p className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-xs text-gray-500">Ayar denetimi yapılamadı: {(q.error as Error).message}</p>;
  const items = q.data ?? [];
  const warns = items.filter((x) => x.level === "warn");
  return (
    <div className={`rounded-xl border px-4 py-3 ${warns.length ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}>
      <p className={`text-sm font-semibold ${warns.length ? "text-amber-900" : "text-emerald-800"}`}>
        Ayar denetimi (kayıtlı hâli): {warns.length ? `${warns.length} uyarı` : "Rexven kurallarına uygun"}
      </p>
      <ul className="mt-1.5 space-y-0.5">
        {items.map((x) => (
          <li key={x.label} className={`text-xs ${x.level === "warn" ? "text-amber-800" : "text-emerald-700"}`}>
            • <b>{x.label}:</b> {x.detail}
          </li>
        ))}
      </ul>
    </div>
  );
}

function TemplateEditor({
  editId,
  initial,
  onBack,
}: {
  editId: string | null;
  initial: TemplateFormData;
  onBack: () => void;
}) {
  const queryClient = useQueryClient();
  // Taki dikeyinde sablona "tarif" alanlari eklenir (sahne seti, fiyat kurali)
  const { isConceptVertical, shop, vertical } = useShopVertical();
  // Tekstil/hediyelik: sablonun urun tipi (taslaga ve Etsy section secimine gecer)
  const { data: productTypes = [] } = useQuery({
    queryKey: ["product-types", vertical],
    queryFn: async (): Promise<Array<{ key: string; label: string }>> => {
      const r = await fetch(`${getBackendUrl()}/api/product-types?vertical=${vertical}`, { headers: authHeaders() });
      if (!r.ok) return [];
      return ((await r.json()).data ?? []) as Array<{ key: string; label: string }>;
    },
    enabled: !isConceptVertical,
    staleTime: 5 * 60_000,
  });
  // Etsy profilleri MAGAZAYA ozel: onbellek anahtarina magaza girmezse
  // magaza degisince eski magazanin profilleri gorunuyordu.
  const shopKey = shop?.id ?? "no-shop";
  const { data: conceptCollectionsData } = useQuery({
    queryKey: ["concept-collections"],
    queryFn: () => apiClient.listMockupCollections("concept"),
    enabled: isConceptVertical,
  });
  const conceptCollections = conceptCollectionsData ?? [];
  // Tekstil/hediyelik: sablona baglanacak MOCKUP gruplari
  const { data: mockupCollectionsData } = useQuery({
    queryKey: ["mockup-collections", "template"],
    queryFn: () => apiClient.listMockupCollections("mockup"),
    enabled: !isConceptVertical,
  });
  const mockupCollections = mockupCollectionsData ?? [];
  const [form, setForm] = useState<TemplateFormData>({ ...initial });
  // Tam ekran gorsel (sablondaki gorsel izgarasi)
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [activeSection, setActiveSection] = useState<SectionKey>("images");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState("");
  const [materialInput, setMaterialInput] = useState("");
  const [newOptionInputs, setNewOptionInputs] = useState<Record<number, string>>({});
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const imageFileRef = useRef<HTMLInputElement>(null);
  const videoFileRef = useRef<HTMLInputElement>(null);

  // Shipping profiles from Etsy
  const { data: shippingProfiles, refetch: refetchProfiles } = useQuery({
    queryKey: ["etsy-shipping-profiles", shopKey],
    queryFn: fetchShippingProfiles,
    staleTime: 5 * 60 * 1000,
  });

  // Production partners from Etsy
  const { data: productionPartners, refetch: refetchPartners, isLoading: partnersLoading } = useQuery({
    queryKey: ["etsy-production-partners", shopKey],
    queryFn: fetchProductionPartners,
    staleTime: 5 * 60 * 1000,
  });

  // Shop sections from Etsy
  const { data: shopSections, refetch: refetchSections } = useQuery({
    queryKey: ["etsy-shop-sections", shopKey],
    queryFn: fetchShopSections,
    staleTime: 5 * 60 * 1000,
  });

  // Return policies from Etsy
  const { data: returnPolicies } = useQuery({
    queryKey: ["etsy-return-policies", shopKey],
    queryFn: fetchReturnPolicies,
    staleTime: 5 * 60 * 1000,
  });

  // Readiness states (processing profiles) from Etsy
  const { data: readinessStates, refetch: refetchReadinessStates } = useQuery({
    queryKey: ["etsy-readiness-states", shopKey],
    queryFn: fetchReadinessStates,
    staleTime: 5 * 60 * 1000,
  });

  // Envanter: isleme profili gecersizse (bos ya da eski "made_to_order_1_3"
  // gibi yayinda yok sayilan metin) magazanin profiliyle doldurulur —
  // once "made_to_order", yoksa ilki.
  useEffect(() => {
    if (!readinessStates?.length) return;
    setForm((f) => {
      if (readinessStates.some((r) => String(r.readiness_state_id) === f.processingProfileId)) return f;
      const pick = readinessStates.find((r) => r.readiness_state === "made_to_order") ?? readinessStates[0];
      return { ...f, processingProfileId: String(pick.readiness_state_id) };
    });
  }, [readinessStates]);
  // Urun ortagi: YENI sablonda bossa magazanin ilk ortagi secilir (mevcut
  // sablonda bilerek "Hicbiri" birakilmis olabilir, ona dokunulmaz)
  useEffect(() => {
    if (editId || !productionPartners?.length) return;
    setForm((f) => (f.productionPartnerId ? f : { ...f, productionPartnerId: String(productionPartners[0].production_partner_id) }));
  }, [productionPartners, editId]);

  // Taxonomy from Etsy
  const { data: taxonomyNodes } = useQuery({
    queryKey: ["etsy-taxonomy", shopKey],
    queryFn: fetchTaxonomyNodes,
    staleTime: 30 * 60 * 1000,
  });
  const flatTaxonomy = useMemo(() => taxonomyNodes ? flattenTaxonomy(taxonomyNodes) : [], [taxonomyNodes]);
  const [taxonomySearch, setTaxonomySearch] = useState("");
  const [taxonomyOpen, setTaxonomyOpen] = useState(false);

  const updateForm = (partial: Partial<TemplateFormData>) => setForm((prev) => ({ ...prev, ...partial }));
  // Etsy kategori ozelliklerinin sablon varsayilanlari ("Ad::Deger" dizisi)
  const { vertical: tplVertical } = useShopVertical();
  const tplTaxQ = useTaxonomyProps(form.taxonomyId ? Number(form.taxonomyId) : null);
  const tplDefaults = parseTemplateDefaults(form.attributes[TEMPLATE_DEFAULTS_KEY]);
  const tplDefaultProps = tplTaxQ.data ? templateProps(tplTaxQ.data, tplDefaults) : [];
  const setTplDefaultProps = (next: ListingProp[]) => {
    const names = new Map((tplTaxQ.data ?? []).map((t) => [t.property_id, t.name]));
    const map: Record<string, string[]> = {};
    for (const p of next) map[names.get(p.propertyId) ?? p.name] = p.values;
    const list = serializeTemplateDefaults(map);
    setForm((f) => ({ ...f, attributes: { ...f.attributes, [TEMPLATE_DEFAULTS_KEY]: list.length ? list : undefined } }));
  };

  // IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const key = entry.target.getAttribute("data-section");
            if (key) setActiveSection(key as SectionKey);
          }
        }
      },
      { rootMargin: "-80px 0px -60% 0px", threshold: 0 }
    );
    for (const key of Object.keys(sectionRefs.current)) {
      const el = sectionRefs.current[key];
      if (el) { el.setAttribute("data-section", key); observer.observe(el); }
    }
    return () => observer.disconnect();
  }, []);

  function buildPayload() {
    return {
      name: form.name.trim(), description: form.description.trim(), tags: form.tags,
      category: form.category || null, price: form.price ? Number(form.price) : null,
      sku: form.sku || null,
      brandModel: form.brandModel.trim() || null,
      whoMade: form.whoMade, whatIsIt: form.whatIsIt, whenMade: form.whenMade,
      isSupply: form.isSupply, isDigital: form.isDigital, autoRenew: form.autoRenew,
      isFeatured: form.isFeatured,
      shippingProfileId: form.shippingProfileId || null,
      taxonomyId: form.taxonomyId ? Number(form.taxonomyId) : null,
      quantity: Number(form.quantity) || 1, listingType: form.listingType,
      variations: form.variations.length > 0 ? form.variations : null,
      disabledCombos: form.disabledCombos.length > 0 ? form.disabledCombos : null,
      personalization: form.personalization.enabled
        ? { enabled: true, questions: form.personalization.questions }
        : null,
      imageUrls: form.imageUrls, videoUrl: form.videoUrl || null,
      materials: form.materials,
      attributes: (() => {
        const a: Record<string, unknown> = { ...form.attributes };
        if (form.imageLabels.length > 0) a.imageLabels = form.imageLabels;
        return Object.keys(a).length > 0 ? a : null;
      })(),
      productTypeKey: form.productTypeKey || null,
      conceptCollectionId: form.conceptCollectionId || null,
      mockupCollectionIds: form.mockupCollectionIds ?? [],
      priceMultiplier: form.priceMultiplier ? Number(form.priceMultiplier) : null,
      optionRefs: form.optionRefs.length > 0 ? form.optionRefs : null,
      sectionId: form.sectionId || null, processingProfileId: form.processingProfileId || null,
      productionPartnerId: form.productionPartnerId || null, returnPolicyId: form.returnPolicyId || null,
    };
  }

  async function handleSave() {
    if (!form.name.trim()) { setSaveMsg("Şablon adı zorunlu"); setTimeout(() => setSaveMsg(null), 3000); return; }
    // Etsy yuva sinirlari: 1 yuva urunun fiyat eksenine ayrili
    const varRefs = form.optionRefs.filter((r) => r.role === "variation").length;
    const persoRefs = form.optionRefs.filter((r) => r.role === "personalization").length;
    if (varRefs > 2 || persoRefs > 5) {
      setSaveMsg(varRefs > 2
        ? "En fazla 2 varyasyon seçeneği eklenebilir (1. yuva ürünün fiyat ekseni)."
        : "En fazla 5 kişiselleştirme alanı eklenebilir.");
      setTimeout(() => setSaveMsg(null), 6000);
      return;
    }
    if (comboCount(form.variations) > ETSY_MAX_COMBOS) {
      setSaveMsg(`${comboCount(form.variations)} kombinasyon — Etsy en fazla ${ETSY_MAX_COMBOS} kabul ediyor. Varyasyon seçeneklerini azaltın.`);
      sectionRefs.current.variations?.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => setSaveMsg(null), 6000);
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await updateTemplate(editId, buildPayload());
        setSaveMsg("Güncellendi");
      } else {
        await createTemplate(buildPayload());
        setSaveMsg("Oluşturuldu");
      }
      queryClient.invalidateQueries({ queryKey: ["listing-templates"] });
      setTimeout(() => onBack(), 800);
    } catch { setSaveMsg("Kaydetme hatası"); }
    setSaving(false);
    setTimeout(() => setSaveMsg(null), 3000);
  }

  function addTag() {
    const t = tagInput.trim().toLowerCase();
    if (t && !form.tags.includes(t) && form.tags.length < 13) { updateForm({ tags: [...form.tags, t] }); setTagInput(""); }
  }
  function addMaterial() {
    const m = materialInput.trim();
    if (m && !form.materials.includes(m) && form.materials.length < 13) { updateForm({ materials: [...form.materials, m] }); setMaterialInput(""); }
  }

  // Sablon icinde: tablolar once, "info" (talimat/bakim) en sonda
  function sortImagesChartLast(urls: string[], labels: string[]): { imageUrls: string[]; imageLabels: string[] } {
    return placeChartImages(urls, labels);
  }


  function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files) return;
    for (let i = 0; i < files.length; i++) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const url = ev.target?.result as string;
        setForm((prev) => {
          const newUrls = [...prev.imageUrls, url].slice(0, 20);
          const newLabels = [...prev.imageLabels, "size-color-chart"].slice(0, 20);
          return { ...prev, ...sortImagesChartLast(newUrls, newLabels) };
        });
      };
      reader.readAsDataURL(files[i]);
    }
    if (imageFileRef.current) imageFileRef.current.value = "";
  }

  function moveImage(from: number, to: number) {
    if (to < 0 || to >= form.imageUrls.length) return;
    const urls = [...form.imageUrls];
    const labels = [...form.imageLabels];
    [urls[from], urls[to]] = [urls[to], urls[from]];
    [labels[from], labels[to]] = [labels[to], labels[from]];
    updateForm({ imageUrls: urls, imageLabels: labels });
  }

  function removeImage(index: number) {
    updateForm({
      imageUrls: form.imageUrls.filter((_, i) => i !== index),
      imageLabels: form.imageLabels.filter((_, i) => i !== index),
    });
  }

  function setImageLabel(index: number, label: string) {
    const labels = [...form.imageLabels];
    while (labels.length <= index) labels.push("");
    labels[index] = label;
    const sorted = sortImagesChartLast([...form.imageUrls], labels);
    updateForm(sorted);
  }

  function handleVideoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    updateForm({ videoUrl: url });
    if (videoFileRef.current) videoFileRef.current.value = "";
  }

  // Saved variation templates
  const { data: varTemplates } = useQuery({
    queryKey: ["variation-templates"],
    queryFn: fetchVarTemplates,
    staleTime: 60_000,
  });

  function applyVarTemplate(t: VarTemplate) {
    // Baz fiyati sablonun EN DUSUK sellingPrice'ina ayarla ki Etsy tablosundaki
    // her hucre (baz + priceAdjust) sablonda girilen fiyata birebir esit olsun.
    const selling = t.variations
      .flatMap((v) => v.options.map((o) => (o as VarTemplateOption).sellingPrice))
      .filter((p): p is number => typeof p === "number" && p > 0);
    const base = selling.length ? Math.min(...selling) : null;
    updateForm({
      variations: t.variations as Variation[],
      disabledCombos: t.disabledCombos ?? [],
      ...(base != null ? { price: String(base) } : {}),
    });
  }

  function addVariation() { updateForm({ variations: [...form.variations, { name: "Size", options: [{ label: "" }] }] }); }
  function removeVariation(vi: number) { updateForm({ variations: form.variations.filter((_, i) => i !== vi) }); }
  function updateVariationName(vi: number, name: string) { const vars = [...form.variations]; vars[vi] = { ...vars[vi], name }; updateForm({ variations: vars }); }
  function addOption(vi: number) {
    const label = (newOptionInputs[vi] || "").trim();
    if (!label) return;
    const vars = [...form.variations]; vars[vi] = { ...vars[vi], options: [...vars[vi].options, { label }] };
    updateForm({ variations: vars }); setNewOptionInputs({ ...newOptionInputs, [vi]: "" });
  }
  function removeOption(vi: number, oi: number) { const vars = [...form.variations]; vars[vi] = { ...vars[vi], options: vars[vi].options.filter((_, i) => i !== oi) }; updateForm({ variations: vars }); }
  function updateOptionLabel(vi: number, oi: number, label: string) {
    const vars = [...form.variations]; const opts = [...vars[vi].options]; opts[oi] = { ...opts[oi], label }; vars[vi] = { ...vars[vi], options: opts }; updateForm({ variations: vars });
  }

  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* Tam ekran gorsel — kucuk onizlemede size chart okunmuyor */}
      {lightboxIdx !== null && form.imageUrls[lightboxIdx] && (
        <ImageLightbox
          urls={form.imageUrls}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onIndex={setLightboxIdx}
        />
      )}
      {/* Left Sidebar */}
      <div className="w-56 border-r border-gray-200 bg-white flex-shrink-0 overflow-y-auto">
        <div className="p-4">
          <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4">
            <ArrowLeft className="h-4 w-4" /> Şablonlar
          </button>
          <h2 className="text-sm font-bold text-gray-900 mb-4">Bölüm</h2>
          <nav className="space-y-0.5">
            {SECTIONS.map((sec) => {
              const count = sectionCount(form, sec.key);
              const isActive = activeSection === sec.key;
              return (
                <button key={sec.key} onClick={() => { setActiveSection(sec.key); sectionRefs.current[sec.key]?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors ${
                    isActive ? "text-blue-600 bg-blue-50 font-medium border-l-2 border-blue-500" : "text-gray-600 hover:bg-gray-50 border-l-2 border-transparent"
                  }`}>
                  <span>{sec.label}</span>
                  <span className={`text-xs ${isActive ? "text-blue-500" : "text-gray-400"}`}>({count})</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
          <h1 className="text-lg font-bold text-gray-900 uppercase tracking-wide">
            {editId ? "ŞABLON DÜZENLE" : "YENİ ŞABLON"}
          </h1>
          <div className="flex items-center gap-2">
            {saveMsg && <span className="text-xs text-emerald-600 font-medium">{saveMsg}</span>}
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 px-5 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {editId ? "Güncelle" : "Kaydet"}
            </button>
          </div>
        </div>

        <div className="max-w-4xl mx-auto p-6 space-y-6">
          {editId && <TemplateAuditCard templateId={editId} />}
          {/* Mockup gruplari: sablon secilince gruplar da gelsin — her listede
              yeniden secmek gerekmesin. Birden fazla grup eklenebilir
              (tisort + sweatshirt gibi). */}
          {!isConceptVertical && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="mb-1 text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Layers className="h-4 w-4 text-indigo-500" /> Mockup grupları
              </h2>
              <p className="mb-3 text-[11px] text-gray-500">
                Bu şablon seçildiğinde kullanılacak gruplar. Liste Denetimi ve zincir
                bunları otomatik alır.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {mockupCollections.length === 0 ? (
                  <span className="text-xs text-gray-400">Mockup grubu yok.</span>
                ) : mockupCollections.map((c) => {
                  const on = (form.mockupCollectionIds ?? []).includes(c.id);
                  return (
                    <button
                      key={c.id}
                      onClick={() => updateForm({
                        mockupCollectionIds: on
                          ? (form.mockupCollectionIds ?? []).filter((x) => x !== c.id)
                          : [...(form.mockupCollectionIds ?? []), c.id],
                      })}
                      className={`rounded-lg border px-2.5 py-1 text-xs transition-colors ${
                        on ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {c.name} <span className="text-gray-400">({c.mockups?.length ?? 0})</span>
                    </button>
                  );
                })}
              </div>
              {(form.mockupCollectionIds ?? []).length > 0 && (
                <p className="mt-2 text-[11px] text-gray-500">
                  Toplam {mockupCollections
                    .filter((c) => (form.mockupCollectionIds ?? []).includes(c.id))
                    .reduce((n, c) => n + (c.mockups?.length ?? 0), 0)} mockup
                  {" · "}şablon görselleriyle birlikte 20 sınırı geçmemeli
                </p>
              )}
            </div>
          )}


          {/* Template Name */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <label className="text-sm font-semibold text-gray-700 block mb-2">Şablon Adı *</label>
            <input type="text" value={form.name} onChange={(e) => updateForm({ name: e.target.value })}
              placeholder="Örn: RO Sweatshirt, Unisex Tişört Şablonu"
              className="w-full text-sm border border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-200 focus:outline-none" />
          </div>

          {/* ===== IMAGES ===== */}
          <div ref={(el) => { sectionRefs.current.images = el; }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-gray-400" /> Görseller
              </h2>
              <span className="text-[10px] text-gray-400">{form.imageUrls.length}/20</span>
            </div>
            <p className="text-[10px] text-gray-400 mb-3">Size chart, renk kartelası gibi tüm listelemelerde kullanılacak görseller. Listing'e uygulandığında tablolar 5. sıradan itibaren, "Talimat / bilgi" etiketliler en sona eklenir (takıda hepsi en sona).</p>
            <div className="grid grid-cols-5 gap-3">
              {form.imageUrls.map((url, i) => {
                const label = form.imageLabels[i] || "";
                return (
                  <div key={i} className="relative group rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                    {/* Tikla: tam ekran. Size chart gibi gorseller kucuk
                        onizlemede okunmuyor. */}
                    <img
                      src={imgSrc(url)}
                      alt=""
                      onClick={() => setLightboxIdx(i)}
                      title="Tam ekran göster"
                      className="w-full h-24 cursor-zoom-in object-contain"
                    />
                    {/* Delete button */}
                    <button onClick={() => removeImage(i)}
                      className="absolute top-1 right-1 p-1 rounded bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="h-3 w-3" />
                    </button>
                    {/* Reorder buttons */}
                    <div className="absolute top-1 left-1 flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => moveImage(i, i - 1)} disabled={i === 0}
                        className="p-0.5 rounded bg-white/80 hover:bg-white shadow disabled:opacity-30">
                        <ChevronUp className="h-3 w-3 text-gray-600" />
                      </button>
                      <button onClick={() => moveImage(i, i + 1)} disabled={i === form.imageUrls.length - 1}
                        className="p-0.5 rounded bg-white/80 hover:bg-white shadow disabled:opacity-30">
                        <ChevronDown className="h-3 w-3 text-gray-600" />
                      </button>
                    </div>
                    {/* Label selector */}
                    <select value={label} onChange={(e) => setImageLabel(i, e.target.value)}
                      className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] py-0.5 px-1 border-0 outline-none cursor-pointer appearance-none text-center">
                      <option value="size-color-chart">Size-Color Chart</option>
                      <option value="mockup">Mockup</option>
                      <option value="info">Talimat / bilgi (sonda)</option>
                      <option value="">Diğer</option>
                    </select>
                  </div>
                );
              })}
              {form.imageUrls.length < 20 && (
                <button onClick={() => imageFileRef.current?.click()}
                  className="rounded-lg border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-1 h-24 hover:border-blue-300 hover:bg-blue-50/50 transition-colors">
                  <Upload className="h-5 w-5 text-gray-300" />
                  <span className="text-[10px] text-gray-400">Yükle</span>
                </button>
              )}
            </div>
            <input ref={imageFileRef} type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
          </div>

          {/* ===== VIDEO ===== */}
          <div ref={(el) => { sectionRefs.current.video = el; }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Video className="h-4 w-4 text-gray-400" /> Videolar
              </h2>
              {form.videoUrl && (
                <button onClick={() => updateForm({ videoUrl: "" })} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1">
                  <X className="h-3 w-3" /> Kaldır
                </button>
              )}
            </div>
            {form.videoUrl ? (
              <div className="relative rounded-lg border border-gray-200 overflow-hidden bg-gray-50 max-w-xs">
                <video src={form.videoUrl} className="w-full h-48 object-contain bg-black" controls />
              </div>
            ) : (
              <button onClick={() => videoFileRef.current?.click()}
                className="w-72 h-52 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-3 hover:border-blue-400 hover:bg-blue-50/30 transition-colors cursor-pointer">
                <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center">
                  <Plus className="h-6 w-6 text-white" />
                </div>
                <span className="text-xs text-gray-400">Maksimum dosya boyutu: 100mb</span>
              </button>
            )}
            <input ref={videoFileRef} type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" />
          </div>

          {/* ===== DESCRIPTION ===== */}
          <div ref={(el) => { sectionRefs.current.description = el; }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
              <AlignLeft className="h-4 w-4 text-gray-400" /> Açıklama
            </h2>
            <textarea value={form.description} onChange={(e) => updateForm({ description: e.target.value })} rows={8}
              placeholder={"ÜRÜN HAKKINDA\n[Ürün açıklaması]\n\nİÇERİK\n- [Özellik 1]\n- [Özellik 2]\n\nKARGO\n[Kargo bilgisi]"}
              className="w-full text-sm border border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-200 focus:outline-none resize-y" />
            {/* Taki: AI'nin yazdigi girisin ALTINA eklenen sabit metin */}
            <div className="mt-4">
              <div className="mb-1 flex items-center justify-between">
                <label className="text-xs font-medium text-gray-700">Açıklama alt metni (AI metninin altına eklenir)</label>
                {!String(form.attributes.descriptionFooter ?? "").trim() && (
                  <button type="button" onClick={() => setForm((f) => ({ ...f, attributes: { ...f.attributes, descriptionFooter: DEFAULT_DESCRIPTION_FOOTER } }))}
                    className="text-[11px] font-medium text-blue-600 hover:text-blue-800">Varsayılan metni ekle</button>
                )}
              </div>
              <textarea value={String(form.attributes.descriptionFooter ?? "")}
                onChange={(e) => setForm((f) => ({ ...f, attributes: { ...f.attributes, descriptionFooter: e.target.value || undefined } }))}
                rows={10}
                className="w-full rounded-lg border border-gray-200 px-4 py-3 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-blue-200 resize-y" />
              <p className="mt-1 text-[10px] text-gray-400">
                Üründen doldurulur: {"{siparis}"} sipariş adımları (varyasyonlar + kişiselleştirme), {"{malzeme}"}, {"{renkler}"},
                {" {uzunluk_bolumu}"} (yalnız uzunluk seçeneği olan üründe), {"{tur}"} necklace/earrings/ring/bracelet, {"{urun}"} ürün adı.
              </p>
            </div>
          </div>

          {/* ===== TAGS ===== */}
          <div ref={(el) => { sectionRefs.current.tags = el; }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Tag className="h-4 w-4 text-gray-400" /> Etiketler</h2>
              <span className={`text-[10px] ${form.tags.length >= 13 ? "text-amber-500" : "text-gray-400"}`}>{form.tags.length}/13</span>
            </div>
            <div className="flex flex-wrap gap-1.5 min-h-[28px] mb-3">
              {form.tags.map((t, i) => (
                <span key={i} className="flex items-center gap-1 bg-blue-50 text-blue-700 text-xs px-2.5 py-1 rounded-full border border-blue-200">
                  {t}
                  <button onClick={() => updateForm({ tags: form.tags.filter((_, idx) => idx !== i) })} className="hover:text-red-500"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input type="text" value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                placeholder="Etiket yaz ve Enter" maxLength={20}
                className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none" />
              <button onClick={addTag} disabled={form.tags.length >= 13}
                className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50">Ekle</button>
            </div>
          </div>

          {/* ===== PRICING ===== */}
          <div ref={(el) => { sectionRefs.current.pricing = el; }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3"><DollarSign className="h-4 w-4 text-gray-400" /> Fiyat & Envanter</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Fiyat (USD) *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <input type="number" value={form.price} onChange={(e) => updateForm({ price: e.target.value })}
                      placeholder="19.99" step="0.01" min="0"
                      className="w-full text-sm border border-gray-200 rounded-lg pl-7 pr-4 py-2.5 focus:ring-2 focus:ring-blue-200 focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Miktar *</label>
                  <input type="number" value={form.quantity} onChange={(e) => updateForm({ quantity: e.target.value })}
                    min="1" className="w-full text-sm border border-gray-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-200 focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">SKU</label>
                <input type="text" value={form.sku} onChange={(e) => updateForm({ sku: e.target.value })}
                  placeholder="Örn: TSH-BLK-001" maxLength={32}
                  className="w-64 text-sm border border-gray-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-200 focus:outline-none" />
                <p className="text-[10px] text-gray-400 mt-1">{form.sku.length}/32 &middot; Bu şablondan oluşturulan listelerde SKU başlangıç değeri olarak kullanılır ve sonuna sayı eklenir.</p>
              </div>
              <div>
                <label htmlFor="tpl-brand-model" className="text-xs text-gray-500 block mb-1">Ürün markası / modeli</label>
                <input id="tpl-brand-model" type="text" value={form.brandModel} onChange={(e) => updateForm({ brandModel: e.target.value })}
                  placeholder="Örn: Comfort Colors 1717" maxLength={80}
                  className="w-64 text-sm border border-gray-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-200 focus:outline-none" />
                <p className="text-[10px] text-gray-400 mt-1">Başlık ve açıklama üretilirken bu marka/model kullanılır; ilham listesindeki başka marka yazılmaz.</p>
              </div>
            </div>
          </div>

          {/* ===== INVENTORY ===== */}
          <div ref={(el) => { sectionRefs.current.inventory = el; }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3"><Package className="h-4 w-4 text-gray-400" /> Envanter</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Kim Yaptı</label>
                <select value={form.whoMade} onChange={(e) => updateForm({ whoMade: e.target.value })}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-200 focus:outline-none">
                  <option value="i_did">I did</option>
                  <option value="someone_else">A member of my shop</option>
                  <option value="collective">Another company or person</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Nedir?</label>
                <select value={form.whatIsIt} onChange={(e) => updateForm({ whatIsIt: e.target.value })}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-200 focus:outline-none">
                  <option value="finished_product">A finished product</option>
                  <option value="supply_or_tool">A supply or tool to make things</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Ne Zaman Yapıldı?</label>
                <select value={form.whenMade} onChange={(e) => updateForm({ whenMade: e.target.value })}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-200 focus:outline-none">
                  <option value="made_to_order">Made to Order</option>
                  <option value="2020_2025">2020-2025</option>
                  <option value="2010_2019">2010-2019</option>
                  <option value="before_2010">2010 öncesi</option>
                </select>
              </div>
            </div>
          </div>

          {/* ===== DETAILS (OCL-style) ===== */}
          <div ref={(el) => { sectionRefs.current.details = el; }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-4"><Settings2 className="h-4 w-4 text-gray-400" /> Detaylar</h2>

            <div className="space-y-4">
              {/* Category - Etsy Taxonomy Dropdown */}
              <div className="relative">
                <label className="text-xs text-gray-500 block mb-1">Kategori (Etsy Taxonomy)</label>
                {form.taxonomyId && form.category ? (
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-lg border border-blue-200 flex-1 truncate">
                      {form.category} <span className="text-blue-400">(ID: {form.taxonomyId})</span>
                    </span>
                    <button onClick={() => { updateForm({ taxonomyId: "", category: "" }); setTaxonomyOpen(true); }}
                      className="text-xs text-red-500 hover:text-red-700"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ) : (
                  // Kategorisiz sablonla acilan liste Etsy de yanlis kategoriye
                  // dusuyor ve varyasyon ozellikleri eslenemiyor.
                  <p className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
                    Kategori seçilmedi — bu şablonla açılan listeler yayınlanamaz.
                  </p>
                )}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                  <input type="text" value={taxonomySearch}
                    onChange={(e) => { setTaxonomySearch(e.target.value); setTaxonomyOpen(true); }}
                    onFocus={() => setTaxonomyOpen(true)}
                    placeholder="Kategori ara... (örn: t-shirt, sweatshirt)"
                    className="w-full text-sm border border-gray-200 rounded-lg pl-9 pr-4 py-2.5 focus:ring-2 focus:ring-blue-200 focus:outline-none" />
                </div>
                {taxonomyOpen && taxonomySearch.length >= 2 && (
                  <div className="absolute z-30 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
                    {flatTaxonomy
                      .filter((t) => t.path.toLowerCase().includes(taxonomySearch.toLowerCase()))
                      .slice(0, 50)
                      .map((t) => (
                        <button key={t.id} onClick={() => {
                          updateForm({ taxonomyId: String(t.id), category: t.path });
                          setTaxonomySearch("");
                          setTaxonomyOpen(false);
                        }}
                          className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 border-b border-gray-50 last:border-0">
                          <span className="text-gray-700">{t.path}</span>
                          <span className="text-gray-400 ml-2">({t.id})</span>
                        </button>
                      ))}
                    {flatTaxonomy.filter((t) => t.path.toLowerCase().includes(taxonomySearch.toLowerCase())).length === 0 && (
                      <p className="text-xs text-gray-400 p-3 text-center">Sonuç bulunamadı</p>
                    )}
                  </div>
                )}
                <p className="text-[10px] text-gray-400 mt-1">Etsy'den gelen kategori ağacından seçin</p>
              </div>

              {/* Renewal & Type */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-xs text-gray-500 block mb-2">Yenileme Seçenekleri</label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={form.autoRenew} onChange={() => updateForm({ autoRenew: true })}
                        className="text-blue-600 focus:ring-blue-200" />
                      <span className="text-sm text-gray-700">Otomatik</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={!form.autoRenew} onChange={() => updateForm({ autoRenew: false })}
                        className="text-blue-600 focus:ring-blue-200" />
                      <span className="text-sm text-gray-700">Manuel</span>
                    </label>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-2">Tip</label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={!form.isDigital} onChange={() => updateForm({ isDigital: false, listingType: "physical" })}
                        className="text-blue-600 focus:ring-blue-200" />
                      <span className="text-sm text-gray-700">Fiziksel</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={form.isDigital} onChange={() => updateForm({ isDigital: true, listingType: "digital" })}
                        className="text-blue-600 focus:ring-blue-200" />
                      <span className="text-sm text-gray-700">Dijital</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Production Partner & Section */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs text-gray-500">Ürün Ortağı (İsteğe Bağlı)</label>
                    <button onClick={() => refetchPartners()} className="text-[10px] text-blue-500 hover:text-blue-700 flex items-center gap-0.5">
                      <RefreshCw className={`h-2.5 w-2.5 ${partnersLoading ? "animate-spin" : ""}`} /> Yenile
                    </button>
                  </div>
                  {partnersLoading ? (
                    <div className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 text-gray-400 flex items-center gap-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Yükleniyor...
                    </div>
                  ) : productionPartners && productionPartners.length > 0 ? (
                    <select value={form.productionPartnerId} onChange={(e) => updateForm({ productionPartnerId: e.target.value })}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-200 focus:outline-none">
                      <option value="">Hiçbiri</option>
                      {productionPartners.map((p) => (
                        <option key={p.production_partner_id} value={String(p.production_partner_id)}>
                          {p.partner_name}{p.location ? ` - ${p.location}` : ""}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div>
                      <input type="text" value={form.productionPartnerId} onChange={(e) => updateForm({ productionPartnerId: e.target.value })}
                        placeholder="Etsy ürün ortağı ID'si"
                        className="w-full text-sm border border-gray-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-200 focus:outline-none" />
                      <p className="text-[10px] text-gray-400 mt-1">Etsy bağlantısı yapıldığında ürün ortakları otomatik listelenecek.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Materials */}
              <div>
                <label className="text-xs text-gray-500 block mb-1">Malzemeler (İsteğe Bağlı)</label>
                <div className="flex flex-wrap gap-1.5 min-h-[28px] mb-2">
                  {form.materials.map((m, i) => (
                    <span key={i} className="flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-2.5 py-1 rounded-full">
                      {m}
                      <button onClick={() => updateForm({ materials: form.materials.filter((_, idx) => idx !== i) })} className="hover:text-red-500"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input type="text" value={materialInput} onChange={(e) => setMaterialInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addMaterial(); } }}
                    placeholder="Lütfen Malzeme giriniz..."
                    className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none" />
                </div>
                <p className="text-[10px] text-gray-400 mt-1">13 materyal daha ekleyebilirsiniz</p>
              </div>

              {/* Etsy kategori ozellikleri: bu sablonla acilan taslaklarda varsayilan */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <EtsyPropertiesEditor
                  mode="template"
                  taxProps={tplTaxQ.data} loading={tplTaxQ.isLoading && !!form.taxonomyId}
                  error={tplTaxQ.error instanceof Error ? tplTaxQ.error.message : null}
                  value={tplDefaultProps} onChange={setTplDefaultProps}
                />
                <p className="mt-1 text-[10px] text-gray-400">
                  Kategoride hep aynı olan değerler (alıcı, kapama tipi…). Ürüne özel olanlar (malzeme, ölçü) üründen, kalanlar AI ile doldurulur.
                  {tplVertical === "jewelry" && Object.keys(tplDefaults).length === 0 &&
                    ` Boş bırakılırsa takı varsayılanı: ${Object.entries(JEWELRY_PROPERTY_DEFAULTS).map(([k, v]) => `${k}: ${v.join(", ")}`).join(" · ")}.`}
                </p>
              </div>
            </div>
          </div>

          {/* ===== SECENEKLER =====
               Kutuphanedeki setler bu sablona baglanir ve ROLU burada belirlenir.
               Etsy'de 3 varyasyon yuvasi var; 1.'si urunun FIYAT eksenine ayrili
               oldugu icin buradan en fazla 2 varyasyon secilebilir. */}
          {isConceptVertical && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="mb-1 text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Layers className="h-4 w-4 text-indigo-500" /> Seçenekler
              </h2>
              <p className="mb-3 text-[11px] text-gray-500">
                Kütüphaneden set seç, rolünü belirle. 1. varyasyon yuvası ürünün fiyat eksenine
                ayrılıdır (kaç halka, kaç taş…), buradan en fazla 2 varyasyon eklenebilir.
              </p>
              <TemplateOptionPicker
                value={form.optionRefs}
                onChange={(refs) => updateForm({ optionRefs: refs })}
              />
            </div>
          )}

          {!isConceptVertical && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <label className="mb-1 block text-sm font-semibold text-gray-700" htmlFor="tpl-product-type">Ürün tipi</label>
              <p className="mb-2 text-[11px] text-gray-500">
                Bu şablondan oluşan taslaklara geçer; hediyelikte Etsy bölümü (section) buna göre seçilir.
              </p>
              <select
                id="tpl-product-type"
                value={form.productTypeKey}
                onChange={(e) => updateForm({ productTypeKey: e.target.value })}
                className="w-full max-w-xs rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="">Seç…</option>
                {productTypes.map((p) => <option key={p.key} value={p.key}>{p.label || p.key}</option>)}
                {form.productTypeKey && !productTypes.some((p) => p.key === form.productTypeKey) && (
                  <option value={form.productTypeKey}>{form.productTypeKey}</option>
                )}
              </select>
            </div>
          )}

          {/* ===== TAKI TARIFI =====
               Bir urun grubu (kolye, yuzuk...) icin listeleme kurali: hangi
               sahneler uretilecek ve satis fiyati maliyetin kac kati olacak.
               Listing Builder sablonu secince bunlari otomatik uygular. */}
          {isConceptVertical && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="mb-1 text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-pink-500" /> Takı tarifi
              </h2>
              <p className="mb-3 text-[11px] text-gray-500">
                Şablon seçilince üretilecek sahne seti ve fiyat kuralı da gelir.
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-1 block text-xs text-gray-500">Ürün grubu</label>
                  <select
                    value={form.productTypeKey}
                    onChange={(e) => updateForm({ productTypeKey: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="">Seç…</option>
                    {["kolye", "yuzuk", "kupe", "bileklik", "halhal", "set"].map((k) => (
                      <option key={k} value={k}>{k}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs text-gray-500">Sahne seti (konsept koleksiyonu)</label>
                  <select
                    value={form.conceptCollectionId}
                    onChange={(e) => updateForm({ conceptCollectionId: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="">Seç…</option>
                    {conceptCollections.map((c) => (
                      <option key={c.id} value={c.id}>{c.name} ({c.mockups?.length ?? 0})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs text-gray-500" title="Tüm Etsy ücretleri (işlem %6.5, ödeme %3 + $0.25, listeleme $0.20, regulatory %2.25) düşüldükten sonra satış fiyatının bu yüzdesi net kâr kalır">
                    Hedef kâr % (Etsy)
                  </label>
                  <input
                    type="number" step="1" min="0" max="70"
                    value={form.attributes.targetMarginPct ?? ""}
                    onChange={(e) => setForm((f) => ({ ...f, attributes: { ...f.attributes, targetMarginPct: e.target.value === "" ? undefined : Number(e.target.value) } }))}
                    placeholder="20"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-gray-500">Fiyat çarpanı (Trendyol, maliyet x)</label>
                  <input
                    type="number" step="0.1" min="1"
                    value={form.priceMultiplier}
                    onChange={(e) => updateForm({ priceMultiplier: e.target.value })}
                    placeholder="1.5"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ===== VARIATIONS (OCL-style) ===== */}
          <div ref={(el) => { sectionRefs.current.variations = el; }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Layers className="h-4 w-4 text-gray-400" /> Varyasyonlar</h2>
              <button onClick={addVariation}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium bg-blue-50 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition-colors">
                <Plus className="h-3.5 w-3.5" /> Varyasyon Ekle
              </button>
            </div>
            {/* Saved variation templates */}
            {varTemplates && varTemplates.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="text-[10px] text-gray-400 font-medium self-center">Kayıtlı Şablonlar:</span>
                {varTemplates.map((t) => (
                  <button key={t.id} onClick={() => applyVarTemplate(t)}
                    className={`text-[11px] px-3 py-1.5 rounded-lg border transition-colors ${
                      t.category === "combo-catalog"
                        ? "border-purple-200 hover:border-purple-400 hover:bg-purple-50 text-purple-600"
                        : "border-green-200 hover:border-green-400 hover:bg-green-50 text-green-600"
                    }`}>
                    <Layers className="h-3 w-3 inline mr-1" />{t.name}
                  </button>
                ))}
              </div>
            )}

            {form.variations.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Layers className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Henüz varyasyon eklenmedi</p>
              </div>
            ) : (
              <>
              <div className="mb-3"><EtsyComboLimitNotice variations={form.variations} /></div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {form.variations.map((v, vi) => (
                  <div key={vi} className="border border-gray-200 rounded-xl bg-gray-50 overflow-hidden">
                    <div className="flex items-center justify-between bg-white px-4 py-3 border-b border-gray-200">
                      <select value={v.name} onChange={(e) => updateVariationName(vi, e.target.value)}
                        className="text-sm font-medium border border-gray-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-200 focus:outline-none min-w-[140px]">
                        {["Size", "Color", "Style", "Material", "Length", "Width", "Scent", "Flavor", "Pattern", "Device"].map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                      <button onClick={() => removeVariation(vi)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <div className="p-3 space-y-1.5">
                      {v.options.map((opt, oi) => (
                        <div key={oi} className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-gray-200">
                          <GripVertical className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" />
                          <input type="text" value={opt.label} onChange={(e) => updateOptionLabel(vi, oi, e.target.value)}
                            placeholder="Seçenek adı" className="flex-1 text-sm border-0 focus:ring-0 focus:outline-none bg-transparent p-0" />
                          {v.name === "Size" && (opt as VariationOption & { sellingPrice?: number }).sellingPrice != null && (
                            <span className="text-[10px] font-medium text-green-600 flex-shrink-0">
                              ${((opt as VariationOption & { sellingPrice?: number }).sellingPrice!).toFixed(2)}
                            </span>
                          )}
                          {v.name === "Size" && opt.priceAdjust != null && opt.priceAdjust > 0 && (
                            <span className="text-[10px] text-gray-400 flex-shrink-0">+${opt.priceAdjust.toFixed(2)}</span>
                          )}
                          <button onClick={() => removeOption(vi, oi)} className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600"><X className="h-3 w-3" /></button>
                        </div>
                      ))}
                      <div className="flex items-center gap-2 mt-2">
                        <input type="text" value={newOptionInputs[vi] || ""} onChange={(e) => setNewOptionInputs({ ...newOptionInputs, [vi]: e.target.value })}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOption(vi); } }}
                          placeholder="Seçenek ekle" className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none" />
                        <button onClick={() => addOption(vi)} className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600">Ekle</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              </>
            )}

            {/* Kombinasyonlari yonet — her hucre tiklanip acilir/kapanir.
                "—" hucreler Etsy'de is_enabled=false gonderilir. */}
            {form.variations.length === 2 && form.variations[0].options.length > 0 && form.variations[1].options.length > 0 && (() => {
              const v0 = form.variations[0], v1 = form.variations[1];
              const off = (l0: string, l1: string) => form.disabledCombos.some((d) => d[v0.name] === l0 && d[v1.name] === l1);
              const total = v0.options.length * v1.options.length;
              const active = total - form.disabledCombos.length;
              const toggleCombo = (l0: string, l1: string) => {
                if (off(l0, l1)) {
                  updateForm({ disabledCombos: form.disabledCombos.filter((d) => !(d[v0.name] === l0 && d[v1.name] === l1)) });
                } else {
                  updateForm({ disabledCombos: [...form.disabledCombos, { [v0.name]: l0, [v1.name]: l1 }] });
                }
              };
              return (
                <div className="mt-4 border border-indigo-200 rounded-xl overflow-hidden">
                  <div className="bg-indigo-50 px-4 py-2 border-b border-indigo-200 flex items-center justify-between gap-2">
                    <h3 className="text-xs font-semibold text-indigo-700">Kombinasyonları Yönet — {v0.name} × {v1.name}</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-indigo-500">{active} aktif · {form.disabledCombos.length} devre dışı</span>
                      {total > 400 && (
                        <span className="text-[10px] font-semibold text-red-600" title="Etsy 400'den fazla kombinasyonu (devre dışılar dahil) kabul etmiyor">
                          {total} kombinasyon — Etsy sınırı 400
                        </span>
                      )}
                      {form.disabledCombos.length > 0 && (
                        <button onClick={() => updateForm({ disabledCombos: [] })}
                          className="text-[10px] font-medium text-indigo-600 hover:text-indigo-800 underline">Tümünü aç</button>
                      )}
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="text-[11px] border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="sticky left-0 z-10 bg-gray-50 text-left px-2 py-1.5 font-semibold text-gray-600 whitespace-nowrap">{v0.name} / {v1.name}</th>
                          {v1.options.map((o, j) => (
                            <th key={j} className="px-2 py-1.5 font-medium text-gray-500 whitespace-nowrap">{o.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {v0.options.map((o0, i) => (
                          <tr key={i} className="border-b border-gray-100">
                            <td className="sticky left-0 z-10 bg-white px-2 py-1 font-medium text-gray-700 whitespace-nowrap">{o0.label}</td>
                            {v1.options.map((o1, j) => {
                              const disabled = off(o0.label, o1.label);
                              return (
                                <td key={j} className="px-1 py-1 text-center">
                                  <button
                                    onClick={() => toggleCombo(o0.label, o1.label)}
                                    title={disabled ? "Kapalı — açmak için tıkla" : "Açık — kapatmak için tıkla"}
                                    className={`inline-flex items-center justify-center w-6 h-6 rounded transition-colors ${disabled ? "bg-gray-100 text-gray-300 hover:bg-red-50 hover:text-red-400" : "text-emerald-500 hover:bg-emerald-100"}`}
                                  >
                                    {disabled ? <span>—</span> : <Check className="h-3 w-3" />}
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="px-4 py-2 text-[10px] text-gray-400 border-t border-gray-100">
                    "—" hücreler Etsy'de kapalı gönderilir (is_enabled=false). Combo varyasyon şablonundan gelir; kaydedince korunur.
                  </p>
                </div>
              );
            })()}
          </div>

          {/* ===== PERSONALIZATION ===== */}
          <div ref={(el) => { sectionRefs.current.personalization = el; }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3"><Pencil className="h-4 w-4 text-gray-400" /> Kişiselleştirme</h2>
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input type="checkbox" checked={form.personalization.enabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  const questions = enabled && form.personalization.questions.length === 0
                    ? [{ question_type: "text_input" as const, question_text: "", required: true, max_allowed_characters: 50 }]
                    : form.personalization.questions;
                  updateForm({ personalization: { enabled, questions } });
                }}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-200" />
              <span className="text-sm text-gray-700">Kişiselleştirme aktif</span>
            </label>
            {form.personalization.enabled && (
              <>
                <p className="text-[11px] text-gray-400 mb-3">Etsy en fazla 5 soru destekler: metin, açılır liste ve dosya yükleme. Şablona kaydedince listing oluştururken otomatik gelir.</p>
                <PersonalizationEditor
                  accent="blue"
                  questions={form.personalization.questions}
                  onChange={(questions) => updateForm({ personalization: { ...form.personalization, questions } })}
                />
              </>
            )}
          </div>

          {/* ===== SHIPPING (Processing & Shipping - Etsy style) ===== */}
          <div ref={(el) => { sectionRefs.current.shipping = el; }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Truck className="h-4 w-4 text-gray-400" /> İşleme & Gönderim</h2>
              <button onClick={() => { refetchProfiles(); }} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium">
                <RefreshCw className="h-3 w-3" /> Profilleri Yenile
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mb-4">Kargo profili ve sipariş işleme süresini belirleyin.</p>

            <div className="space-y-4">
              {/* Processing Profile - Etsy Readiness States */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-700">İşleme Profili (Readiness State)</label>
                  <button onClick={() => refetchReadinessStates()} className="flex items-center gap-1 text-[10px] text-blue-500 hover:text-blue-700 font-medium">
                    <RefreshCw className="h-2.5 w-2.5" /> Yenile
                  </button>
                </div>
                {readinessStates && readinessStates.length > 0 ? (
                  <select value={form.processingProfileId} onChange={(e) => updateForm({ processingProfileId: e.target.value })}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-200 focus:outline-none">
                    <option value="">Profil seç</option>
                    {readinessStates.map((rs) => (
                      <option key={rs.readiness_state_id} value={String(rs.readiness_state_id)}>
                        {rs.readiness_state_label || rs.readiness_state}
                        {rs.min_processing_days != null && rs.max_processing_days != null
                          ? ` (${rs.min_processing_days}-${rs.max_processing_days} gün)`
                          : ""}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select value={form.processingProfileId} onChange={(e) => updateForm({ processingProfileId: e.target.value })}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-200 focus:outline-none">
                    <option value="made_to_order_1_2">Made to order - 1-2 days</option>
                    <option value="made_to_order_1_3">Made to order - 1-3 days</option>
                    <option value="made_to_order_3_5">Made to order - 3-5 days</option>
                    <option value="made_to_order_1_7">Made to order - 1-7 days</option>
                    <option value="ready_to_ship_1_2">Ready to ship - 1-2 days</option>
                    <option value="ready_to_ship_1_3">Ready to ship - 1-3 days</option>
                  </select>
                )}
              </div>

              {/* Shipping Option / Profile */}
              <div className="border border-gray-200 rounded-lg p-4">
                <label className="text-xs font-medium text-gray-700 block mb-2">Kargo Seçeneği</label>
                {shippingProfiles && shippingProfiles.length > 0 ? (
                  <select value={form.shippingProfileId} onChange={(e) => updateForm({ shippingProfileId: e.target.value })}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-200 focus:outline-none">
                    <option value="">Profil seç</option>
                    {shippingProfiles.map((p) => (
                      <option key={p.shipping_profile_id} value={String(p.shipping_profile_id)}>
                        {p.title}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div>
                    <input type="text" value={form.shippingProfileId} onChange={(e) => updateForm({ shippingProfileId: e.target.value })}
                      placeholder="Etsy kargo profili ID'si"
                      className="w-full text-sm border border-gray-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-200 focus:outline-none" />
                    <p className="text-[10px] text-gray-400 mt-1">Etsy bağlantısı yapıldığında kargo profilleri otomatik listelenecek.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ===== SETTINGS ===== */}
          <div ref={(el) => { sectionRefs.current.settings = el; }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Settings className="h-4 w-4 text-gray-400" /> Ayarlar</h2>
              <button onClick={() => refetchSections()} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium">
                <RefreshCw className="h-3 w-3" /> Yenile
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mb-4">Mağaza görüntüleme ve yenileme ayarlarını belirleyin.</p>

            <div className="space-y-5">
              {/* Returns and Exchanges - Etsy Return Policies */}
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">İade ve Değişim Politikası *</label>
                <div className="border border-gray-200 rounded-lg p-3">
                  {returnPolicies && returnPolicies.length > 0 ? (
                    <div>
                      <select value={form.returnPolicyId || ""} onChange={(e) => updateForm({ returnPolicyId: e.target.value })}
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-200 focus:outline-none">
                        <option value="">Politika seç</option>
                        {returnPolicies.map((rp) => (
                          <option key={rp.return_policy_id} value={String(rp.return_policy_id)}>
                            ID: {rp.return_policy_id}
                            {rp.accepts_returns && rp.accepts_exchanges ? " - İade ve değişim kabul edilir"
                              : rp.accepts_returns ? " - İade kabul edilir"
                              : rp.accepts_exchanges ? " - Değişim kabul edilir"
                              : " - İade/değişim yok"}
                          </option>
                        ))}
                      </select>
                      <p className="text-[10px] text-gray-400 mt-1">Etsy'deki iade politikalarınızdan seçin</p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-700">
                          {form.returnPolicyId ? `Politika ID: ${form.returnPolicyId}` : "İade politikası seçilmedi"}
                        </p>
                        <p className="text-xs text-gray-400">Etsy bağlantısı yapıldığında politikalar otomatik listelenecek.</p>
                      </div>
                      <input type="text" value={form.returnPolicyId} onChange={(e) => updateForm({ returnPolicyId: e.target.value })}
                        placeholder="Politika ID"
                        className="w-32 text-xs border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none" />
                    </div>
                  )}
                </div>
              </div>

              {/* Shop Section */}
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Mağaza Bölümü</label>
                <p className="text-[10px] text-gray-400 mb-2">Ürünlerinizi alışveriş yapanların gözatabileceği gruplara ayırmak için mağaza bölümlerini kullanın.</p>
                {shopSections && shopSections.length > 0 ? (
                  <select value={form.sectionId} onChange={(e) => updateForm({ sectionId: e.target.value })}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-200 focus:outline-none">
                    <option value="">Hiçbiri</option>
                    {shopSections.map((s) => (
                      <option key={s.shop_section_id} value={String(s.shop_section_id)}>
                        {s.title} ({s.active_listing_count} ürün)
                      </option>
                    ))}
                  </select>
                ) : (
                  <input type="text" value={form.sectionId} onChange={(e) => updateForm({ sectionId: e.target.value })}
                    placeholder="Mağaza bölümü ID'si"
                    className="w-full text-sm border border-gray-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-200 focus:outline-none" />
                )}
              </div>

              {/* Feature this listing */}
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-xs font-medium text-gray-700 block">Bu Listingi Öne Çıkar</label>
                    <p className="text-[10px] text-gray-400">Mağazanızın ana sayfasında öne çıkararak dikkat çekici hale getirin.</p>
                  </div>
                  <button
                    onClick={() => updateForm({ isFeatured: !form.isFeatured })}
                    className={`relative w-11 h-6 rounded-full transition-colors ${form.isFeatured ? "bg-blue-500" : "bg-gray-300"}`}>
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.isFeatured ? "translate-x-5" : ""}`} />
                  </button>
                </div>
              </div>

              {/* Renewal Options */}
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Yenileme Seçenekleri *</label>
                <p className="text-[10px] text-gray-400 mb-2">Her yenileme dört ay sürer veya listing tükenene kadar geçerlidir.</p>
                <div className="space-y-2">
                  <label className={`flex items-start gap-3 rounded-lg border px-4 py-3 cursor-pointer transition-colors ${
                    form.autoRenew ? "border-blue-400 bg-blue-50/50" : "border-gray-200 hover:bg-gray-50"
                  }`}>
                    <input type="radio" name="renewalOption" checked={form.autoRenew}
                      onChange={() => updateForm({ autoRenew: true })}
                      className="mt-0.5 text-blue-600 focus:ring-blue-200" />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Otomatik</span>
                      <p className="text-xs text-gray-400">Bu listing süresi dolduğunda her seferinde 0,20 USD karşılığında yenilenir (önerilen).</p>
                    </div>
                  </label>
                  <label className={`flex items-start gap-3 rounded-lg border px-4 py-3 cursor-pointer transition-colors ${
                    !form.autoRenew ? "border-blue-400 bg-blue-50/50" : "border-gray-200 hover:bg-gray-50"
                  }`}>
                    <input type="radio" name="renewalOption" checked={!form.autoRenew}
                      onChange={() => updateForm({ autoRenew: false })}
                      className="mt-0.5 text-blue-600 focus:ring-blue-200" />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Manuel</span>
                      <p className="text-xs text-gray-400">Süresi dolan listingleri kendim yenileyeceğim.</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// ======== Template List (Table View) ========

function TemplateListView({
  templates,
  loading,
  onEdit,
  onCreate,
}: {
  templates: ListingTemplate[];
  loading: boolean;
  onEdit: (t: ListingTemplate) => void;
  onCreate: () => void;
}) {
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const deleteM = useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["listing-templates"] });
      setDeleteConfirm(null);
    },
  });

  // Kopyala: sablonu bozmadan uzerinde deneme yapabilmek icin
  const duplicateM = useMutation({
    mutationFn: duplicateTemplate,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["listing-templates"] }),
  });

  if (loading) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;
  }

  return (
    <div>
      {templates.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50">
          <FileText className="h-12 w-12 text-gray-300 mb-4" />
          <h3 className="text-base font-semibold text-gray-600 mb-1">Henüz şablon yok</h3>
          <p className="text-sm text-gray-400 mb-4">Listing şablonları oluşturarak zaman kazanın</p>
          <button onClick={onCreate} className="flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-600">
            <Plus className="h-4 w-4" /> İlk Şablonu Oluştur
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Görsel</th>
                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Şablon Adı</th>
                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Başlık</th>
                <th className="text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-3 py-3">Etiket</th>
                <th className="text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-3 py-3">Varyasyon</th>
                <th className="text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-3 py-3">Mockup Grubu</th>
                <th className="text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-3 py-3">Oluşturulma Tarihi</th>
                <th className="text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-3 py-3">Güncellenme Tarihi</th>
                <th className="text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-2 py-3">#</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => {
                const varCount = (t.variations as Variation[] | null)?.length || 0;
                const thumbUrl = t.imageUrls?.[0];
                return (
                  <tr key={t.id} className="border-b border-gray-100 hover:bg-blue-50/40 transition-colors group">
                    <td className="px-4 py-3">
                      <div className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden">
                        {thumbUrl ? (
                          <img src={imgSrc(thumbUrl)} alt="" className="w-full h-full object-contain" />
                        ) : (
                          <FileText className="h-4 w-4 text-gray-300" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-800">{t.name}</p>
                      {t.category && <p className="text-[10px] text-gray-400 mt-0.5">{t.category}</p>}
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className="text-sm text-gray-600 truncate">{t.description ? t.description.slice(0, 60) + (t.description.length > 60 ? "..." : "") : "-"}</p>
                    </td>
                    <td className="px-3 py-3 text-center text-xs text-gray-500">{t.tags.length}</td>
                    <td className="px-3 py-3 text-center text-xs text-gray-500">{varCount}</td>
                    <td className="px-3 py-3 text-center text-xs">
                      {(t.mockupCollectionIds?.length ?? 0) > 0 ? (
                        <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[11px] text-indigo-700">
                          {t.mockupCollectionIds!.length} grup
                        </span>
                      ) : (
                        <span className="text-[11px] text-gray-300" title="Şablonu düzenleyip en üstteki 'Mockup grupları' bölümünden bağla">
                          bağlı değil
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-center text-[10px] text-gray-400">
                      {new Date(t.createdAt).toLocaleDateString("tr-TR")} {new Date(t.createdAt).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td className="px-3 py-3 text-center text-[10px] text-gray-400">
                      {new Date(t.updatedAt).toLocaleDateString("tr-TR")} {new Date(t.updatedAt).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td className="px-2 py-3 text-center">
                      <div className="flex items-center gap-1 justify-center">
                        <button onClick={() => setDeleteConfirm(t.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors" title="Sil">
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => duplicateM.mutate(t.id)}
                          disabled={duplicateM.isPending}
                          className="p-1.5 rounded-lg text-gray-400 transition-colors hover:bg-indigo-50 hover:text-indigo-600 disabled:opacity-50"
                          title="Kopyala"
                        >
                          {duplicateM.isPending && duplicateM.variables === t.id
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <Copy className="h-4 w-4" />}
                        </button>
                        <button onClick={() => onEdit(t)} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-400 hover:text-emerald-600 transition-colors" title="Düzenle">
                          <Pencil className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full m-4 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Şablonu Sil?</h3>
            <p className="text-sm text-gray-600 mb-5">Bu işlem geri alınamaz.</p>
            <div className="flex gap-2">
              <button onClick={() => deleteM.mutate(deleteConfirm)} disabled={deleteM.isPending}
                className="flex items-center gap-1.5 rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-40">
                {deleteM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Sil
              </button>
              <button onClick={() => setDeleteConfirm(null)} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">İptal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ======== Variation Templates List ========

function VariationTemplatesView() {
  const queryClient = useQueryClient();
  const { data: templates, isLoading } = useQuery({
    queryKey: ["variation-templates"],
    queryFn: fetchVarTemplates,
  });

  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);
  const [editPrices, setEditPrices] = useState<VarTemplate["variations"]>([]);
  const [editDisabled, setEditDisabled] = useState<Record<string, string>[]>([]);
  // Otomatik fiyat (Etsy kesinti modeli): hedef kar, alicidan alinan kargo, tedarikci kargosu
  const [autoMargin, setAutoMargin] = useState(20);
  const [autoShipCharge, setAutoShipCharge] = useState(0);
  const [autoShipCost, setAutoShipCost] = useState(DEFAULT_ETSY_SHIPPING_USD);
  // Saklanan fiyat LISTE fiyati; alici bu oranda indirimle oder
  const [autoDiscount, setAutoDiscount] = useState(DEFAULT_DISCOUNT_PCT);
  // Marka duzenleme
  const [editingBrandId, setEditingBrandId] = useState<string | null>(null);
  const [editBrand, setEditBrand] = useState("");
  const [newBrandMode, setNewBrandMode] = useState(false);
  const [newBrandText, setNewBrandText] = useState("");
  const [brandTick, setBrandTick] = useState(0); // localStorage degisince re-render
  const brandOptions = [...DEFAULT_BRANDS, ...getCustomBrands().filter((b) => !DEFAULT_BRANDS.includes(b))];
  void brandTick;

  const deleteM = useMutation({
    mutationFn: deleteVarTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["variation-templates"] });
      setDeleteConfirm(null);
    },
  });

  const updateM = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<{ name: string; brand: string | null; variations: VarTemplate["variations"]; disabledCombos: Record<string, string>[] | null; pricing: VarTemplate["pricing"] }> }) =>
      updateVarTemplate(id, data),
    onError: (e) => alert(e instanceof Error ? e.message : "Varyasyon şablonu güncellenemedi"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["variation-templates"] });
      setEditingId(null);
      setEditingPriceId(null);
      setEditingBrandId(null);
      setNewBrandMode(false);
    },
  });

  // Katalog stogundan disabledCombos'u yeniden hesapla
  const syncM = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/api/variation-templates/${id}/sync-stock`, { method: "POST", headers: getAuthHeaders() });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Senkron başarısız");
      return json;
    },
    onSuccess: (json) => {
      queryClient.invalidateQueries({ queryKey: ["variation-templates"] });
      const updated = json.data ?? json;
      if (Array.isArray(updated?.disabledCombos)) setEditDisabled(updated.disabledCombos);
    },
  });

  const startEditBrand = (t: VarTemplate) => {
    setEditingBrandId(t.id);
    setEditBrand(t.brand || "");
    setNewBrandMode(false);
    setNewBrandText("");
  };
  const saveBrand = (id: string, brand: string) => {
    const b = brand.trim();
    if (b) addCustomBrand(b);
    setBrandTick((n) => n + 1);
    updateM.mutate({ id, data: { brand: b || null } });
  };

  const startEditName = (t: VarTemplate) => {
    setEditingId(t.id);
    setEditName(t.name);
  };

  const startEditPrices = (t: VarTemplate) => {
    setEditingPriceId(t.id);
    setEditPrices(JSON.parse(JSON.stringify(t.variations)));
    setEditDisabled(JSON.parse(JSON.stringify(t.disabledCombos ?? [])));
    // Kayitli hesap ayarlari; yoksa varsayilanlar
    const pr = t.pricing ?? {};
    setAutoMargin(pr.marginPct ?? 20);
    setAutoShipCharge(pr.shippingCharge ?? 0);
    setAutoDiscount(pr.discountPct ?? DEFAULT_DISCOUNT_PCT);
    setAutoShipCost(pr.shippingCost ?? DEFAULT_ETSY_SHIPPING_USD);
  };
  const toggleEditCombo = (v0: string, l0: string, v1: string, l1: string) => {
    setEditDisabled((prev) => {
      const isOff = prev.some((d) => d[v0] === l0 && d[v1] === l1);
      return isOff ? prev.filter((d) => !(d[v0] === l0 && d[v1] === l1)) : [...prev, { [v0]: l0, [v1]: l1 }];
    });
  };

  // Secenegi sil (Etsy 400 kombinasyon siniri icin) — o secenege ait devre disi
  // kombinasyonlar da temizlenir, fiyat farklari yeni en dusuk fiyata gore yeniden hesaplanir.
  const removeEditOption = (varIdx: number, optIdx: number) => {
    const varName = editPrices[varIdx]?.name;
    const label = editPrices[varIdx]?.options[optIdx]?.label;
    setEditPrices((prev) => {
      const next: VarTemplate["variations"] = JSON.parse(JSON.stringify(prev));
      next[varIdx].options.splice(optIdx, 1);
      const opts = next[varIdx].options;
      if (opts.length > 0 && opts.some((o: VarTemplateOption) => o.sellingPrice != null)) {
        const minPrice = Math.min(...opts.map((o: VarTemplateOption) => o.sellingPrice ?? 0));
        for (const opt of opts) opt.priceAdjust = Math.round(((opt.sellingPrice ?? 0) - minPrice) * 100) / 100;
      }
      return next;
    });
    if (varName && label != null) setEditDisabled((prev) => prev.filter((d) => d[varName] !== label));
  };

  const updateOptionField = (varIdx: number, optIdx: number, field: "label" | "sellingPrice" | "priceAdjust", value: string | number) => {
    setEditPrices((prev) => {
      const next: VarTemplate["variations"] = JSON.parse(JSON.stringify(prev));
      if (field === "priceAdjust") {
        // 1. eksen disindaki eksenlerin (Print Side) sabit farki: kombinasyon = 1. eksen fiyati + fark
        next[varIdx].options[optIdx].priceAdjust = Math.max(0, Math.round((Number(value) || 0) * 100) / 100);
      } else if (field === "sellingPrice") {
        const newPrice = Number(value) || 0;
        next[varIdx].options[optIdx].sellingPrice = newPrice;
        // Recalc all priceAdjust based on new min
        const allPrices = next[varIdx].options.map((o: VarTemplateOption) => o.sellingPrice ?? 0);
        const minPrice = Math.min(...allPrices);
        for (const opt of next[varIdx].options) {
          opt.priceAdjust = Math.round(((opt.sellingPrice ?? 0) - minPrice) * 100) / 100;
        }
      } else {
        next[varIdx].options[optIdx].label = value as string;
      }
      return next;
    });
  };

  // 1. eksenin satis fiyatlarini maliyetten hesapla (maliyeti olan secenekler)
  const applyAutoPrices = () => {
    setEditPrices((prev) => {
      const next: VarTemplate["variations"] = JSON.parse(JSON.stringify(prev));
      const opts = next[0]?.options ?? [];
      for (const o of opts) {
        if (typeof o.cost !== "number") continue;
        const sale = etsyPriceFor({ productCost: o.cost, shippingCost: autoShipCost, shippingCharge: autoShipCharge, marginPct: autoMargin });
        o.sellingPrice = listPriceFor(sale, autoDiscount);
      }
      const priced = opts.filter((o) => typeof o.sellingPrice === "number");
      if (priced.length) {
        const min = Math.min(...priced.map((o) => o.sellingPrice!));
        for (const o of priced) o.priceAdjust = Math.round((o.sellingPrice! - min) * 100) / 100;
      }
      return next;
    });
  };

  const duplicateM = useMutation({
    mutationFn: async (t: VarTemplate) => {
      const res = await fetch(`${API_BASE}/api/variation-templates`, {
        method: "POST", headers: getAuthHeaders(),
        body: JSON.stringify({ name: `${t.name} (kopya)`, brand: t.brand ?? undefined, category: t.category, variations: t.variations, disabledCombos: t.disabledCombos ?? undefined }),
      });
      if (!res.ok) throw new Error("Kopyalama başarısız");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["variation-templates"] });
    },
  });

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>;
  }

  const items = templates || [];

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50">
        <Layers className="h-12 w-12 text-gray-300 mb-4" />
        <h3 className="text-base font-semibold text-gray-600 mb-1">Henüz varyasyon şablonu yok</h3>
        <p className="text-sm text-gray-400">Tedarikçi Katalog sayfasından varyasyon oluşturup kaydedin</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((t) => {
        const isEditingName = editingId === t.id;
        const isEditingPrice = editingPriceId === t.id;

        return (
          <div key={t.id} className="rounded-xl border border-gray-200 bg-white shadow-sm">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {isEditingName ? (
                  <div className="flex items-center gap-2 flex-1">
                    <input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="flex-1 rounded border border-indigo-300 px-2 py-1 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-indigo-400"
                      autoFocus
                      onKeyDown={(e) => { if (e.key === "Enter") updateM.mutate({ id: t.id, data: { name: editName } }); if (e.key === "Escape") setEditingId(null); }}
                    />
                    <button onClick={() => updateM.mutate({ id: t.id, data: { name: editName } })} className="rounded bg-indigo-600 px-2 py-1 text-xs text-white hover:bg-indigo-700">
                      <Save className="h-3 w-3" />
                    </button>
                    <button onClick={() => setEditingId(null)} className="text-xs text-gray-400 hover:text-gray-600">İptal</button>
                  </div>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-gray-900 truncate">{t.name}</p>
                    <button onClick={() => startEditName(t)} className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                      <Pencil className="h-3 w-3" />
                    </button>
                  </>
                )}
                {t.category && (
                  <span className={`flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${
                    t.category === "combo-catalog" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"
                  }`}>
                    {t.category === "combo-catalog" ? "Combo" : t.category === "supplier-catalog" ? "Katalog" : t.category}
                  </span>
                )}
                {/* Marka */}
                {editingBrandId === t.id ? (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {newBrandMode ? (
                      <input
                        value={newBrandText}
                        onChange={(e) => setNewBrandText(e.target.value)}
                        placeholder="Yeni marka"
                        autoFocus
                        onKeyDown={(e) => { if (e.key === "Enter") saveBrand(t.id, newBrandText); if (e.key === "Escape") setEditingBrandId(null); }}
                        className="w-28 rounded border border-emerald-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-400"
                      />
                    ) : (
                      <select
                        value={editBrand}
                        onChange={(e) => { if (e.target.value === "__new__") { setNewBrandMode(true); } else setEditBrand(e.target.value); }}
                        className="rounded border border-emerald-300 px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-400"
                      >
                        <option value="">(marka yok)</option>
                        {brandOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                        <option value="__new__">+ Yeni marka…</option>
                      </select>
                    )}
                    <button onClick={() => saveBrand(t.id, newBrandMode ? newBrandText : editBrand)} className="rounded bg-emerald-600 px-2 py-1 text-xs text-white hover:bg-emerald-700">
                      <Save className="h-3 w-3" />
                    </button>
                    <button onClick={() => { setEditingBrandId(null); setNewBrandMode(false); }} className="text-xs text-gray-400 hover:text-gray-600">İptal</button>
                  </div>
                ) : t.brand ? (
                  <button onClick={() => startEditBrand(t)} title="Markayı düzenle"
                    className="flex-shrink-0 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-100">
                    {t.brand}
                  </button>
                ) : (
                  <button onClick={() => startEditBrand(t)} title="Marka ekle"
                    className="flex-shrink-0 rounded border border-dashed border-gray-300 px-1.5 py-0.5 text-[10px] text-gray-400 hover:border-emerald-300 hover:text-emerald-600">
                    + Marka
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                <span className="text-[10px] text-gray-400 mr-2">
                  {new Date(t.updatedAt).toLocaleDateString("tr-TR")}
                </span>
                <button
                  onClick={() => duplicateM.mutate(t)}
                  disabled={duplicateM.isPending}
                  className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors"
                  title="Kopyala"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => isEditingPrice ? setEditingPriceId(null) : startEditPrices(t)}
                  className={`p-1.5 rounded-lg transition-colors ${isEditingPrice ? "bg-indigo-100 text-indigo-600" : "hover:bg-gray-100 text-gray-400 hover:text-gray-600"}`}
                  title="Düzenle"
                >
                  <Settings className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => setDeleteConfirm(t.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600" title="Sil">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-4 py-3">
              {isEditingPrice ? (
                <div className="space-y-3">
                  <EtsyComboLimitNotice variations={editPrices} />
                  {editPrices[0]?.options.some((o) => typeof o.cost === "number") && (
                    <div className="flex flex-wrap items-end gap-2 rounded-lg border border-indigo-100 bg-indigo-50/40 p-2 text-xs">
                      <label className="flex flex-col gap-0.5 text-[10px] text-gray-600">Hedef kâr (%)
                        <input type="number" min={0} max={80} value={autoMargin} onChange={(e) => setAutoMargin(Number(e.target.value))}
                          className="w-16 rounded border border-gray-300 px-1.5 py-0.5 text-xs" />
                      </label>
                      <label className="flex flex-col gap-0.5 text-[10px] text-gray-600">Alıcıdan alınan kargo ($)
                        <span className="flex gap-1">
                          <input type="number" min={0} step={0.5} value={autoShipCharge} onChange={(e) => setAutoShipCharge(Math.max(0, Number(e.target.value)))}
                            className="w-16 rounded border border-gray-300 px-1.5 py-0.5 text-xs" />
                          <button type="button" onClick={() => setAutoShipCharge(0)}
                            className={`rounded border px-1.5 text-[10px] ${autoShipCharge === 0 ? "border-green-300 bg-green-50 text-green-700" : "border-gray-300 text-gray-600"}`}>Ücretsiz</button>
                        </span>
                      </label>
                      <label className="flex flex-col gap-0.5 text-[10px] text-gray-600">İndirim (%)
                        <input type="number" min={0} max={90} step={5} value={autoDiscount} onChange={(e) => setAutoDiscount(Math.min(90, Math.max(0, Number(e.target.value))))}
                          className="w-16 rounded border border-gray-300 px-1.5 py-0.5 text-xs" />
                      </label>
                      <label className="flex flex-col gap-0.5 text-[10px] text-gray-600">Tedarikçi kargosu ($)
                        <input type="number" min={0} step={0.5} value={autoShipCost} onChange={(e) => setAutoShipCost(Number(e.target.value))}
                          className="w-16 rounded border border-gray-300 px-1.5 py-0.5 text-xs" />
                      </label>
                      <button type="button" onClick={applyAutoPrices}
                        className="rounded bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-indigo-700">
                        Fiyatları hesapla
                      </button>
                      <p className="w-full text-[10px] text-gray-500">
                        {editPrices[0].name} liste fiyatları maliyet + kargo + Etsy kesintileri (KDV dahil) üzerinden hedef kâra göre hesaplanır; alıcı %{autoDiscount} indirimle öder. Kargo ücretsizse kargo maliyeti fiyata biner.
                      </p>
                    </div>
                  )}
                  {editPrices.map((v, vi) => (
                    <div key={vi}>
                      <p className="mb-1.5 text-xs font-semibold text-gray-500 uppercase">{v.name} ({v.options.length})</p>
                      <div className="overflow-hidden rounded-lg border border-gray-200">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Etiket</th>
                              {vi === 0 && v.options.some((o) => typeof o.cost === "number") && (
                                <th className="px-2 py-1.5 text-right font-medium text-gray-600 text-[10px]" title="Tedarikçi ürün maliyeti + tedarikçi kargosu (yukarıdaki alan)">Maliyet + kargo</th>
                              )}
                              {vi === 0 && <th className="px-2 py-1.5 text-right font-medium text-gray-600">Liste ($)</th>}
                              {vi === 0 && <th className="px-2 py-1.5 text-right font-medium text-gray-600 text-[10px]">İndirimli</th>}
                              {vi === 0 && autoShipCharge > 0 && <th className="px-2 py-1.5 text-right font-medium text-gray-600 text-[10px]" title="Alıcının ödediği: indirimli satış + kargo">Satış + kargo</th>}
                              {vi === 0 && <th className="px-2 py-1.5 text-right font-medium text-gray-600 text-[10px]">Fark</th>}
                              {vi === 0 && v.options.some((o) => typeof o.cost === "number") && (
                                <th className="px-2 py-1.5 text-right font-medium text-gray-600 text-[10px]" title="Maliyet + tedarikçi kargosu + Etsy kesintileri düşüldükten sonra (yukarıdaki kargo ayarlarıyla)">Net kâr</th>
                              )}
                              {vi > 0 && v.name !== "Color" && <th className="px-2 py-1.5 text-right font-medium text-gray-600 text-[10px]">Fark (+$)</th>}
                              <th className="w-6" />
                            </tr>
                          </thead>
                          <tbody>
                            {v.options.map((opt, oi) => (
                              <tr key={oi} className={oi % 2 === 0 ? "" : "bg-gray-50/50"}>
                                <td className="px-2 py-1">
                                  <input
                                    value={opt.label}
                                    onChange={(e) => updateOptionField(vi, oi, "label", e.target.value)}
                                    className="w-full rounded border border-gray-200 px-1.5 py-0.5 text-xs focus:border-indigo-400 focus:outline-none"
                                  />
                                </td>
                                {vi === 0 && v.options.some((o) => typeof o.cost === "number") && (
                                  <td className="whitespace-nowrap px-2 py-1 text-right text-[10px] text-gray-600">
                                    {typeof opt.cost === "number" ? `$${opt.cost.toFixed(2)} + $${autoShipCost.toFixed(2)}` : "—"}
                                  </td>
                                )}
                                {vi === 0 && (
                                  <td className="px-2 py-1">
                                    <input
                                      type="number"
                                      value={opt.sellingPrice ?? 0}
                                      onChange={(e) => updateOptionField(vi, oi, "sellingPrice", e.target.value)}
                                      className="w-20 rounded border border-gray-200 px-1.5 py-0.5 text-right text-xs font-medium focus:border-indigo-400 focus:outline-none"
                                      step={0.01}
                                      min={0}
                                    />
                                  </td>
                                )}
                                {vi === 0 && (
                                  <td className="px-2 py-1 text-right text-[10px] text-gray-600">
                                    {opt.sellingPrice ? `$${discountedPrice(opt.sellingPrice, autoDiscount).toFixed(2)}` : "—"}
                                  </td>
                                )}
                                {vi === 0 && autoShipCharge > 0 && (
                                  <td className="whitespace-nowrap px-2 py-1 text-right text-[10px] text-gray-600">
                                    {opt.sellingPrice ? `$${discountedPrice(opt.sellingPrice, autoDiscount).toFixed(2)} + $${autoShipCharge.toFixed(2)}` : "—"}
                                  </td>
                                )}
                                {vi === 0 && (
                                  <td className="px-2 py-1 text-right text-[10px] text-gray-400">
                                    {opt.priceAdjust ? `+${opt.priceAdjust.toFixed(2)}` : "—"}
                                  </td>
                                )}
                                {vi === 0 && v.options.some((o) => typeof o.cost === "number") && (
                                  <td className="px-2 py-1 text-right text-[10px]">
                                    {typeof opt.cost === "number" && opt.sellingPrice ? (() => {
                                      const r = etsyNetProfit({ price: discountedPrice(opt.sellingPrice, autoDiscount), productCost: opt.cost, shippingCost: autoShipCost, shippingCharge: autoShipCharge });
                                      return <span className={r.net < 0 ? "font-medium text-red-600" : "text-green-700"}>${r.net.toFixed(2)} · %{r.marginPct.toFixed(0)}</span>;
                                    })() : "—"}
                                  </td>
                                )}
                                {vi > 0 && v.name !== "Color" && (
                                  <td className="px-2 py-1">
                                    <input
                                      type="number"
                                      value={opt.priceAdjust ?? 0}
                                      onChange={(e) => updateOptionField(vi, oi, "priceAdjust", e.target.value)}
                                      aria-label={`${opt.label} fiyat farkı`}
                                      className="w-20 rounded border border-gray-200 px-1.5 py-0.5 text-right text-xs focus:border-indigo-400 focus:outline-none"
                                      step={0.5}
                                      min={0}
                                    />
                                  </td>
                                )}
                                <td className="px-1 py-1 text-center">
                                  <button onClick={() => removeEditOption(vi, oi)} disabled={v.options.length <= 1}
                                    title="Bu seçeneği şablondan sil"
                                    className="rounded p-0.5 text-gray-300 hover:bg-red-50 hover:text-red-500 disabled:opacity-30">
                                    <X className="h-3 w-3" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}

                  {/* Kombinasyonlari yonet — 2 varyasyon (Size × Color) */}
                  {editPrices.length === 2 && editPrices[0].options.length > 0 && editPrices[1].options.length > 0 && (() => {
                    const v0 = editPrices[0], v1 = editPrices[1];
                    const off = (l0: string, l1: string) => editDisabled.some((d) => d[v0.name] === l0 && d[v1.name] === l1);
                    const total = v0.options.length * v1.options.length;
                    const active = total - editDisabled.length;
                    return (
                      <div className="border border-indigo-200 rounded-lg overflow-hidden">
                        <div className="bg-indigo-50 px-3 py-1.5 border-b border-indigo-200 flex items-center justify-between gap-2">
                          <span className="text-[11px] font-semibold text-indigo-700">Kombinasyonları Yönet — {v0.name} × {v1.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-indigo-500">{active} aktif · {editDisabled.length} devre dışı</span>
                            {total > 400 && (
                              <span className="text-[10px] font-semibold text-red-600" title="Etsy 400'den fazla kombinasyonu (devre dışılar dahil) kabul etmiyor">
                                {total} kombinasyon — Etsy sınırı 400
                              </span>
                            )}
                            {t.syncSource && (
                              <button onClick={() => syncM.mutate(t.id)} disabled={syncM.isPending}
                                title="Kaynak kataloğun güncel stoğuna göre stokta olmayan kombinasyonları devre dışı yap"
                                className="flex items-center gap-1 text-[10px] font-medium text-emerald-600 hover:text-emerald-800 underline disabled:opacity-50">
                                {syncM.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Stoktan güncelle
                              </button>
                            )}
                            {editDisabled.length > 0 && (
                              <button onClick={() => setEditDisabled([])} className="text-[10px] font-medium text-indigo-600 hover:text-indigo-800 underline">Tümünü aç</button>
                            )}
                          </div>
                        </div>
                        <div className="overflow-x-auto max-h-72">
                          <table className="text-[11px] border-collapse">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="sticky left-0 z-10 bg-gray-50 text-left px-2 py-1 font-semibold text-gray-600 whitespace-nowrap">{v0.name} / {v1.name}</th>
                                {v1.options.map((o, j) => <th key={j} className="px-1 py-1 font-medium text-gray-500 whitespace-nowrap">{o.label}</th>)}
                              </tr>
                            </thead>
                            <tbody>
                              {v0.options.map((o0, i) => (
                                <tr key={i} className="border-b border-gray-100">
                                  <td className="sticky left-0 z-10 bg-white px-2 py-1 font-medium text-gray-700 whitespace-nowrap">{o0.label}</td>
                                  {v1.options.map((o1, j) => {
                                    const disabled = off(o0.label, o1.label);
                                    return (
                                      <td key={j} className="px-1 py-1 text-center">
                                        <button onClick={() => toggleEditCombo(v0.name, o0.label, v1.name, o1.label)}
                                          title={disabled ? "Kapalı — açmak için tıkla" : "Açık — kapatmak için tıkla"}
                                          className={`inline-flex items-center justify-center w-6 h-6 rounded transition-colors ${disabled ? "bg-gray-100 text-gray-300 hover:bg-red-50 hover:text-red-400" : "text-emerald-500 hover:bg-emerald-100"}`}>
                                          {disabled ? <span>—</span> : <Check className="h-3 w-3" />}
                                        </button>
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p className="px-3 py-1.5 text-[10px] text-gray-400 border-t border-gray-100">"—" hücreler Etsy'de kapalı (is_enabled=false) gönderilir.</p>
                      </div>
                    );
                  })()}

                  <div className="flex gap-2">
                    <button
                      onClick={() => updateM.mutate({ id: t.id, data: {
                        variations: editPrices, disabledCombos: editDisabled,
                        pricing: { marginPct: autoMargin, shippingCharge: autoShipCharge, discountPct: autoDiscount, shippingCost: autoShipCost },
                      } })}
                      disabled={updateM.isPending || comboCount(editPrices) > ETSY_MAX_COMBOS}
                      title={comboCount(editPrices) > ETSY_MAX_COMBOS ? `Etsy sınırı: en fazla ${ETSY_MAX_COMBOS} kombinasyon` : undefined}
                      className="flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {updateM.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                      Kaydet
                    </button>
                    <button onClick={() => setEditingPriceId(null)} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50">
                      İptal
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {t.variations.map((v, vi) => (
                    <div key={vi}>
                      <p className="mb-1 text-[10px] font-semibold text-gray-400 uppercase">{v.name} ({v.options.length})</p>
                      <div className="flex flex-wrap gap-1">
                        {v.options.map((opt, oi) => (
                          <span key={oi} className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] text-gray-700">
                            {opt.label}
                            {vi === 0 && opt.sellingPrice != null && (
                              <span className="ml-1 font-medium text-green-600" title={`Liste fiyatı; %${autoDiscount} indirimle $${discountedPrice(opt.sellingPrice, autoDiscount).toFixed(2)}`}>
                                ${opt.sellingPrice.toFixed(2)} <span className="font-normal text-gray-400">→ ${discountedPrice(opt.sellingPrice, autoDiscount).toFixed(2)}</span>
                              </span>
                            )}
                            {opt.sellingPrice == null && opt.priceAdjust != null && opt.priceAdjust > 0 && (
                              <span className="ml-1 text-gray-400">+${opt.priceAdjust.toFixed(2)}</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full m-4 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Şablonu Sil?</h3>
            <p className="text-sm text-gray-600 mb-5">Bu işlem geri alınamaz.</p>
            <div className="flex gap-2">
              <button onClick={() => deleteM.mutate(deleteConfirm)} disabled={deleteM.isPending}
                className="flex items-center gap-1.5 rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-40">
                {deleteM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Sil
              </button>
              <button onClick={() => setDeleteConfirm(null)} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">İptal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ======== Main Page ========

export default function ListingTemplatesPage() {
  // Sablon yapisi PAZARYERINE gore degisir: Etsy'de taksonomi + kargo profili,
  // Trendyol'da kategori + o kategorinin zorunlu ozellikleri. Bu yuzden iki
  // ayri duzenleyici var; hangisinin acilacagini aktif magaza belirler.
  const { platform } = useShopVertical();
  const queryClient = useQueryClient();
  const [view, setView] = useState<"list" | "editor">("list");
  const [tab, setTab] = useState<"listing" | "variation" | "options" | "assets">("listing");
  const [editId, setEditId] = useState<string | null>(null);
  const [initialForm, setInitialForm] = useState<TemplateFormData>(EMPTY_FORM);

  const { data: templates, isLoading } = useQuery({
    queryKey: ["listing-templates"],
    queryFn: fetchTemplates,
  });

  // Baska ekrandan (Liste Denetimi) "Sablon olustur" ile gelindi: form onceden
  // dolu acilir, kaydedince/vazgecince geri donulur.
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const returnTo = params.get("returnTo");
  useEffect(() => {
    if (params.get("new") !== "1") return;
    setEditId(null);
    setInitialForm({ ...EMPTY_FORM, productTypeKey: params.get("productTypeKey") ?? "", taxonomyId: params.get("taxonomyId") ?? "" });
    setView("editor");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // ?edit=<id>: sablonlar yuklenince o sablonun formu acilir (Liste Denetimi'nden)
  const editParam = params.get("edit");
  const [editOpened, setEditOpened] = useState(false);
  useEffect(() => {
    if (!editParam || editOpened || !templates?.length) return;
    const t = templates.find((x) => x.id === editParam);
    if (t) { setEditOpened(true); openEdit(t); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editParam, templates, editOpened]);
  const backFromEditor = () => {
    if (returnTo && returnTo.startsWith("/")) navigate(returnTo);
    else setView("list");
  };

  function openCreate() {
    setEditId(null);
    setInitialForm({ ...EMPTY_FORM });
    setView("editor");
  }

  function openEdit(t: ListingTemplate) {
    const variations = (t.variations as Variation[] | null) || [];
    const pers = normalizePersonalization(t.personalization as Parameters<typeof normalizePersonalization>[0]);
    const attrs = (t.attributes as TemplateAttributes) || {};
    // Baz fiyati varyasyon sellingPrice'larindan (min) turet — Etsy tablosu dogru gelsin
    const varSelling = variations.flatMap((v) => (v.options || []).map((o) => (o as { sellingPrice?: number }).sellingPrice)).filter((p): p is number => typeof p === "number" && p > 0);
    const derivedBase = varSelling.length ? Math.min(...varSelling) : null;
    setEditId(t.id);
    setInitialForm({
      name: t.name, description: t.description, tags: t.tags,
      category: t.category || "", price: derivedBase != null ? String(derivedBase) : (t.price ? String(t.price) : ""), sku: t.sku || "", brandModel: t.brandModel || "",
      whoMade: t.whoMade, whatIsIt: t.whatIsIt || "finished_product", whenMade: t.whenMade,
      isSupply: t.isSupply, isDigital: t.isDigital ?? false, autoRenew: t.autoRenew ?? true,
      isFeatured: t.isFeatured ?? false,
      shippingProfileId: t.shippingProfileId || "", taxonomyId: t.taxonomyId ? String(t.taxonomyId) : "",
      quantity: String(t.quantity || 999), listingType: t.listingType || "physical",
      variations,
      disabledCombos: (t.disabledCombos as Record<string, string>[]) ?? [],
      personalization: { enabled: pers.enabled, questions: pers.questions },
      imageUrls: t.imageUrls || [],
      imageLabels: (Array.isArray((t.attributes as Record<string, unknown>)?.imageLabels) ? (t.attributes as Record<string, unknown>).imageLabels as string[] : (t.imageUrls || []).map(() => "size-color-chart")),
      videoUrl: t.videoUrl || "",
      materials: t.materials || [], attributes: attrs,
      sectionId: t.sectionId || "", processingProfileId: t.processingProfileId || "",
      productionPartnerId: t.productionPartnerId || "", returnPolicyId: t.returnPolicyId || "",
      productTypeKey: t.productTypeKey || "",
      conceptCollectionId: t.conceptCollectionId || "",
      mockupCollectionIds: t.mockupCollectionIds ?? [],
      priceMultiplier: t.priceMultiplier != null ? String(t.priceMultiplier) : "",
      optionRefs: ((t as unknown as { optionRefs?: OptionRef[] }).optionRefs) ?? [],
    });
    setView("editor");
  }

  if (view === "editor" && platform === "trendyol") {
    return (
      <TrendyolTemplateEditor
        onBack={() => setView("list")}
        onSaved={() => { setView("list"); queryClient.invalidateQueries({ queryKey: ["listing-templates"] }); }}
      />
    );
  }

  if (view === "editor") {
    return <TemplateEditor editId={editId} initial={initialForm} onBack={backFromEditor} />;
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><FileText className="h-5 w-5 text-blue-500" /> Şablonlar</h1>
          <p className="text-sm text-gray-500 mt-1">Listing ve varyasyon şablonlarınızı yönetin</p>
        </div>
        {tab === "listing" && (
          <button onClick={openCreate} className="flex items-center gap-2 rounded-lg bg-blue-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-600 transition-colors">
            <Plus className="h-4 w-4" /> {platform === "trendyol" ? "Yeni Trendyol Şablonu" : "Yeni Listing Şablonu"}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="mb-5 flex gap-1 rounded-lg bg-gray-100 p-1">
        <button
          onClick={() => setTab("listing")}
          className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            tab === "listing" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <FileText className="mr-1.5 inline h-4 w-4" />
          Listing Şablonları ({templates?.length || 0})
        </button>
        <button
          onClick={() => setTab("variation")}
          className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            tab === "variation" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <Layers className="mr-1.5 inline h-4 w-4" />
          Varyasyon Şablonları
        </button>
        <button
          onClick={() => setTab("options")}
          className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            tab === "options" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <Sparkles className="mr-1.5 inline h-4 w-4" />
          Seçenek Kütüphanesi
        </button>
        <button
          onClick={() => setTab("assets")}
          className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            tab === "assets" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <FileText className="mr-1.5 inline h-4 w-4" />
          Görsel Kütüphanesi
        </button>
      </div>

      {tab === "listing" ? (
        <TemplateListView templates={templates || []} loading={isLoading} onEdit={openEdit} onCreate={openCreate} />
      ) : tab === "options" ? (
        <OptionSetsView />
      ) : tab === "assets" ? (
        <div>
          <p className="mb-3 text-sm text-gray-500">
            Renk kartelası, beden tablosu gibi birden fazla listede kullanılan görseller. Bir <b>şablona</b> eklenince o şablonla açılan
            tüm listelere girer; tek tek <b>tasarımlara</b> Liste Denetimi'nden atanır (listede satan mockup'tan sonra).
          </p>
          <AssetLibrary templates={(templates || []).map((t) => ({ id: t.id, name: t.name }))} />
        </div>
      ) : (
        <VariationTemplatesView />
      )}
    </div>
  );
}


/** Tam ekran gorsel — ok tuslariyla gezilir, ESC/tiklama ile kapanir. */
function ImageLightbox({ urls, index, onClose, onIndex }: {
  urls: string[];
  index: number;
  onClose: () => void;
  onIndex: (i: number) => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onIndex((index + 1) % urls.length);
      if (e.key === "ArrowLeft") onIndex((index - 1 + urls.length) % urls.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, urls.length, onClose, onIndex]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <img
        src={imgSrc(urls[index])}
        alt=""
        onClick={(e) => e.stopPropagation()}
        className="max-h-[92vh] max-w-[92vw] object-contain"
      />
      <button onClick={onClose} className="absolute right-4 top-4 rounded-full bg-white/15 p-2 text-white hover:bg-white/25">
        <X className="h-5 w-5" />
      </button>
      {urls.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onIndex((index - 1 + urls.length) % urls.length); }}
            className="absolute left-4 rounded-full bg-white/15 p-2 text-white hover:bg-white/25"
          >
            <ChevronUp className="h-5 w-5 -rotate-90" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onIndex((index + 1) % urls.length); }}
            className="absolute right-4 top-1/2 rounded-full bg-white/15 p-2 text-white hover:bg-white/25"
          >
            <ChevronDown className="h-5 w-5 -rotate-90" />
          </button>
          <span className="absolute bottom-4 text-xs text-white/80">{index + 1} / {urls.length}</span>
        </>
      )}
    </div>
  );
}


// ======== Secenek Kutuphanesi ========
//
// Urunler arasi ORTAK deger kumeleri: renk, zincir uzunlugu, materyal, dogum
// tasi aylari, isim alani. Urune ozgu yapilandirma eksenleri (1 Halka-2
// Birthstone, Cat&Disc) buraya girmez — onlar katalogtan fiyatiyla gelir.
//
// Rol (varyasyon / kisisellestirme) burada yalnizca VARSAYILAN olarak durur;
// asil karar listede verilir, cunku ayni set bir uruncde fiyatli varyasyon,
// digerinde sadece secim olabiliyor.
interface OptionSet {
  id: string;
  name: string;
  /** Etsy'de musteriye gorunen baslik (FINISH, LENGTH) */
  etsyName?: string | null;
  kind: "list" | "text" | "upload" | "chart";
  values: Array<{ label: string; value?: string }>;
  productTypeKey: string | null;
  defaultRole: "variation" | "personalization";
  maxChars: number | null;
  note: string | null;
  /** Bu secenek kullanildiginda urun gorsellerine eklenecek aciklayici gorsel */
  chartImageUrl?: string | null;
  /** Chart'in AI ozeti (liste analizi sirasinda yazilir) */
  chartSummary?: { url?: string; text?: string } | null;
}

const OPTION_KIND_LABEL: Record<string, string> = {
  list: "Liste",
  text: "Metin",
  upload: "Yükleme",
  chart: "Görsel (chart)",
};

function OptionSetsView() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<OptionSet | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: sets = [], isLoading } = useQuery({
    queryKey: ["option-sets"],
    queryFn: async (): Promise<OptionSet[]> => {
      const r = await fetch(`${API_BASE}/api/option-sets`, { headers: getAuthHeaders() });
      if (!r.ok) return [];
      return (await r.json()).data ?? [];
    },
  });
  // Kutuphane: urune ozel setler (inLibrary=false) listelenmez
  const librarySets = sets.filter((x) => (x as { inLibrary?: boolean }).inLibrary !== false);

  // Chart analizi Gorsel Kutuphanesi'nde tutulur (tek yer): ayni gorsel orada
  // varsa onun ozeti gosterilir; yoksa analiz edilirken oraya eklenir
  const { data: assets = [] } = useAssets();
  const assetByUrl = new Map(assets.map((a) => [a.imageUrl, a]));
  const [analyzing, setAnalyzing] = useState<ListingAsset | null>(null);
  const [linking, setLinking] = useState<string | null>(null);
  const [linkErr, setLinkErr] = useState<string | null>(null);
  const chartSummaryOf = (o: OptionSet) =>
    assetByUrl.get(o.chartImageUrl ?? "")?.summary || (o.chartSummary?.url === o.chartImageUrl ? o.chartSummary?.text : null) || null;
  async function openChartAnalysis(o: OptionSet) {
    const existing = assetByUrl.get(o.chartImageUrl ?? "");
    if (existing) { setAnalyzing(existing); return; }
    setLinking(o.id); setLinkErr(null);
    try {
      const a = await assetApi.create({ name: o.name, imageUrl: o.chartImageUrl! });
      await qc.invalidateQueries({ queryKey: ["listing-assets"] });
      setAnalyzing(a);
    } catch (e) { setLinkErr(e instanceof Error ? e.message : String(e)); } finally { setLinking(null); }
  }

  const seedM = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${API_BASE}/api/option-sets/seed-jewelry`, { method: "POST", headers: getAuthHeaders() });
      if (!r.ok) throw new Error("Oluşturulamadı");
      return (await r.json()).data as { created: string[] };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["option-sets"] }),
  });

  // Seti kopyala ("… (kopya)") — benzer set icin sifirdan yazmamak
  const duplicateM = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`${API_BASE}/api/option-sets/${id}/duplicate`, { method: "POST", headers: getAuthHeaders() });
      if (!r.ok) throw new Error("Kopyalanamadı");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["option-sets"] }),
  });

  const deleteM = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`${API_BASE}/api/option-sets/${id}`, { method: "DELETE", headers: getAuthHeaders() });
      if (!r.ok) throw new Error("Silinemedi");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["option-sets"] }),
  });

  if (isLoading) {
    return <div className="flex items-center gap-2 py-12 text-sm text-gray-400"><Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…</div>;
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button
          onClick={() => { setEditing(null); setCreating(true); }}
          className="flex items-center gap-1.5 rounded-lg bg-blue-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-600"
        >
          <Plus className="h-4 w-4" /> Yeni seçenek seti
        </button>
        <button
          onClick={() => seedM.mutate()}
          disabled={seedM.isPending}
          title="Renk, zincir uzunluğu, materyal, zincir tipi, doğum taşı ve isim alanını hazır oluştur"
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          {seedM.isPending ? "…" : "Takı setlerini hazır kur"}
        </button>
        {seedM.data && (
          <span className="text-xs text-emerald-600">
            {seedM.data.created.length > 0 ? `${seedM.data.created.join(", ")} eklendi` : "Hepsi zaten vardı"}
          </span>
        )}
      </div>

      {librarySets.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center">
          <p className="text-sm text-gray-500">Henüz seçenek seti yok.</p>
          <p className="mt-1 text-xs text-gray-400">
            "Takı setlerini hazır kur" ile renk, zincir uzunluğu, materyal, zincir tipi, doğum taşı ve isim alanını oluştur.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {librarySets.map((o) => (
            <div key={o.id} className="rounded-xl border border-gray-200 bg-white p-3">
              <div className="mb-1 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900">
                    {o.name}
                    {o.etsyName && <span className="ml-1 text-[10px] font-normal text-gray-400">→ {o.etsyName}</span>}
                  </p>
                  <p className="text-[10px] text-gray-400">
                    {OPTION_KIND_LABEL[o.kind] ?? o.kind}
                    {o.kind === "list" ? ` · ${o.values.length} değer` : o.maxChars ? ` · ${o.maxChars} karakter` : ""}
                    {o.productTypeKey ? ` · ${o.productTypeKey}` : ""}
                  </p>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                  o.defaultRole === "variation" ? "bg-indigo-50 text-indigo-700" : "bg-amber-50 text-amber-700"
                }`}>
                  {o.defaultRole === "variation" ? "varyasyon" : "kişiselleştirme"}
                </span>
              </div>

              {o.kind === "list" && (
                <div className="mb-2 flex flex-wrap gap-1">
                  {o.values.slice(0, 8).map((v) => (
                    <span key={v.label} className="rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-600">{v.label}</span>
                  ))}
                  {o.values.length > 8 && <span className="text-[10px] text-gray-400">+{o.values.length - 8}</span>}
                </div>
              )}
              {o.chartImageUrl && (
                <a href={o.chartImageUrl.startsWith("http") ? o.chartImageUrl : `${API_BASE}${o.chartImageUrl}`} target="_blank" rel="noreferrer"
                  title="Chart görseli — listede ürün fotoğraflarının sonuna eklenir" className="mb-2 block w-fit">
                  <img src={o.chartImageUrl.startsWith("http") ? o.chartImageUrl : `${API_BASE}${o.chartImageUrl}`} alt=""
                    className="h-20 max-w-full rounded-lg border border-gray-200 object-contain" />
                </a>
              )}
              {o.chartImageUrl && (
                <button onClick={() => void openChartAnalysis(o)} disabled={linking === o.id}
                  title="Chart'ın AI analizini gör, düzenle, ek notla yeniden analiz et"
                  className="mb-2 flex w-full items-start gap-1.5 text-left text-xs text-gray-500 hover:text-indigo-700 disabled:opacity-50">
                  {linking === o.id ? <Loader2 className="mt-0.5 h-3 w-3 shrink-0 animate-spin" /> : <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-indigo-500" />}
                  <span className={`line-clamp-2 ${chartSummaryOf(o) ? "" : "italic"}`}>{chartSummaryOf(o) || "Chart analiz edilmedi — analiz et"}</span>
                </button>
              )}
              {o.note && <p className="mb-2 text-[10px] text-gray-500">{o.note}</p>}

              <div className="flex gap-1.5">
                <button onClick={() => { setCreating(false); setEditing(o); }}
                  className="rounded border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50">
                  Düzenle
                </button>
                <button onClick={() => duplicateM.mutate(o.id)} disabled={duplicateM.isPending}
                  className="rounded border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                  Kopyala
                </button>
                <button onClick={() => deleteM.mutate(o.id)} disabled={deleteM.isPending}
                  className="rounded border border-gray-200 px-2 py-1 text-[11px] text-red-500 hover:bg-red-50 disabled:opacity-50">
                  Sil
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {linkErr && <p className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{linkErr}</p>}
      {analyzing && (
        <AnalysisModal asset={analyzing} onClose={() => setAnalyzing(null)}
          onSaved={() => void qc.invalidateQueries({ queryKey: ["listing-assets"] })} />
      )}
      {(creating || editing) && (
        <OptionSetEditor
          initial={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { qc.invalidateQueries({ queryKey: ["option-sets"] }); setCreating(false); setEditing(null); }}
        />
      )}
    </div>
  );
}

function OptionSetEditor({ initial, onClose, onSaved }: {
  initial: OptionSet | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [etsyName, setEtsyName] = useState(initial?.etsyName ?? "");
  const [kind, setKind] = useState<OptionSet["kind"]>(initial?.kind ?? "list");
  const [valuesText, setValuesText] = useState((initial?.values ?? []).map((v) => v.label).join("\n"));
  const [role, setRole] = useState<OptionSet["defaultRole"]>(initial?.defaultRole ?? "variation");
  const [productTypeKey, setProductTypeKey] = useState(initial?.productTypeKey ?? "");
  const [maxChars, setMaxChars] = useState(initial?.maxChars ? String(initial.maxChars) : "20");
  const [note, setNote] = useState(initial?.note ?? "");
  const [chartImageUrl, setChartImageUrl] = useState(initial?.chartImageUrl ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const values = valuesText.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean);
  const tooLong = values.filter((v) => v.length > 20);

  async function save() {
    setBusy(true); setErr(null);
    try {
      const body = {
        name, etsyName: etsyName || null, kind, defaultRole: role,
        chartImageUrl: chartImageUrl || null,
        productTypeKey: productTypeKey || null,
        values: values.map((label) => ({ label })),
        maxChars: kind === "text" ? Number(maxChars) || null : null,
        note: note || null,
      };
      const r = await fetch(
        initial ? `${API_BASE}/api/option-sets/${initial.id}` : `${API_BASE}/api/option-sets`,
        { method: initial ? "PATCH" : "POST", headers: getAuthHeaders(), body: JSON.stringify(body) },
      );
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.error || "Kaydedilemedi");
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Kaydedilemedi");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-white p-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-3 text-sm font-semibold text-gray-900">
          {initial ? "Seçenek setini düzenle" : "Yeni seçenek seti"}
        </h3>

        <label className="mb-2 block text-xs text-gray-500">
          Ad (panelde)
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Zincir Uzunluğu"
            className="mt-0.5 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
        </label>

        <label className="mb-2 block text-xs text-gray-500">
          Etsy başlığı (müşteriye görünen)
          <input value={etsyName} onChange={(e) => setEtsyName(e.target.value)} placeholder="Length"
            className="mt-0.5 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          <span className="mt-0.5 block text-[10px] text-gray-400">
            Etsy'de varyasyonun üstünde bu yazar (FINISH, LENGTH). Boş bırakılırsa panel adı kullanılır.
          </span>
        </label>

        <div className="mb-2 grid grid-cols-2 gap-2">
          <label className="block text-xs text-gray-500">
            Tip
            <select value={kind} onChange={(e) => setKind(e.target.value as OptionSet["kind"])}
              className="mt-0.5 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm">
              <option value="list">Liste (seçenekler)</option>
              <option value="text">Metin (isim/harf)</option>
              <option value="upload">Yükleme (fotoğraf)</option>
              <option value="chart">Görsel (seçim yok, sadece chart)</option>
            </select>
          </label>
          <label className={`block text-xs text-gray-500 ${kind === "chart" ? "opacity-40" : ""}`}>
            Varsayılan rol
            <select value={role} onChange={(e) => setRole(e.target.value as OptionSet["defaultRole"])}
              disabled={kind === "chart"}
              title={kind === "chart" ? "Chart tipinde seçim sunulmaz, yalnızca görsel eklenir" : undefined}
              className="mt-0.5 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm disabled:bg-gray-50">
              <option value="variation">Varyasyon</option>
              <option value="personalization">Kişiselleştirme</option>
            </select>
          </label>
        </div>

        {kind === "list" ? (
          <label className="mb-2 block text-xs text-gray-500">
            Değerler (her satır bir değer)
            <textarea value={valuesText} onChange={(e) => setValuesText(e.target.value)} rows={6}
              placeholder={"Gold\nSilver\nRose Gold"}
              className="mt-0.5 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
            <span className="mt-0.5 block text-[10px] text-gray-400">{values.length} değer · Etsy dropdown sınırları: en fazla 30 değer, her biri 20 karakter</span>
            {tooLong.length > 0 && (
              <span className="mt-0.5 block text-[10px] text-amber-600">
                {tooLong.length} değer 20 karakteri aşıyor — kişiselleştirme dropdown'unda kullanılamaz, varyasyon olarak sorun değil
              </span>
            )}
          </label>
        ) : kind === "text" ? (
          <label className="mb-2 block text-xs text-gray-500">
            Karakter sınırı
            <input type="number" min={1} max={1024} value={maxChars} onChange={(e) => setMaxChars(e.target.value)}
              className="mt-0.5 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </label>
        ) : null}

        <label className="mb-2 block text-xs text-gray-500">
          Ürün tipi (boş: hepsinde kullanılır)
          <input value={productTypeKey} onChange={(e) => setProductTypeKey(e.target.value)} placeholder="kolye"
            className="mt-0.5 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
        </label>

        <div className="mb-2 block text-xs text-gray-500">
          {kind === "chart" ? "Chart görseli (zorunlu)" : "Chart görseli (ops.)"}
          <ImageSourcePicker value={chartImageUrl} onChange={setChartImageUrl} name={name.trim() || undefined}
            kind={kind === "chart" ? "size_chart" : "other"} />
          <span className="mt-0.5 block text-[10px] text-gray-400">
            Zincir uzunluğu cetveli, renk kartelası… Bu seçenek listede kullanılınca görsel, ürün fotoğraflarının SONUNA eklenir.
          </span>
        </div>

        <label className="mb-3 block text-xs text-gray-500">
          Not (ops.)
          <input value={note} onChange={(e) => setNote(e.target.value)}
            className="mt-0.5 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
        </label>

        {err && <p className="mb-2 text-xs text-red-600">{err}</p>}

        <div className="flex gap-2">
          <button onClick={save} disabled={busy || !name.trim() || (kind === "list" && values.length === 0) || (kind === "chart" && !chartImageUrl.trim())}
            className="flex-1 rounded-lg bg-blue-500 px-3 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-50">
            {busy ? "…" : "Kaydet"}
          </button>
          <button onClick={onClose} className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
            İptal
          </button>
        </div>
      </div>
    </div>
  );
}


/** Sablona secenek seti baglama: hangi set, hangi rol.
 *
 *  Rolun sette degil BURADA durmasinin sebebi: ayni dogum tasi seti bir uruncde
 *  fiyatli varyasyon, digerinde sadece secim olabiliyor. Sette yalnizca
 *  varsayilan rol var, secildiginde o geliyor ve degistirilebiliyor. */
function TemplateOptionPicker({ value, onChange }: {
  value: OptionRef[];
  onChange: (refs: OptionRef[]) => void;
}) {
  const { data: sets = [] } = useQuery({
    queryKey: ["option-sets"],
    queryFn: async (): Promise<OptionSet[]> => {
      const r = await fetch(`${API_BASE}/api/option-sets`, { headers: getAuthHeaders() });
      if (!r.ok) return [];
      return (await r.json()).data ?? [];
    },
  });

  const { data: presets = [], refetch: refetchPresets } = useQuery({
    queryKey: ["option-presets"],
    queryFn: async (): Promise<Array<{ id: string; name: string; refs: OptionRef[] }>> => {
      const r = await fetch(`${API_BASE}/api/option-sets/presets/list`, { headers: getAuthHeaders() });
      if (!r.ok) return [];
      return (await r.json()).data ?? [];
    },
  });

  async function savePreset() {
    const name = window.prompt("Kalıp adı", "Kolye seti")?.trim();
    if (!name) return;
    await fetch(`${API_BASE}/api/option-sets/presets/save`, {
      method: "POST", headers: getAuthHeaders(),
      body: JSON.stringify({ name, refs: value }),
    });
    refetchPresets();
  }

  const byId = new Map(sets.map((s) => [s.id, s]));
  const used = new Set(value.map((r) => r.optionSetId));
  const available = sets.filter((s) => !used.has(s.id) && (s as { inLibrary?: boolean }).inLibrary !== false);
  const isChart = (id: string) => byId.get(id)?.kind === "chart";
  const variationCount = value.filter((r) => r.role === "variation" && !isChart(r.optionSetId)).length;
  const persoCount = value.filter((r) => r.role === "personalization" && !isChart(r.optionSetId)).length;

  function add(id: string) {
    const set = byId.get(id);
    if (!set) return;
    onChange([...value, { optionSetId: id, role: set.defaultRole }]);
  }

  return (
    <div>
      {/* Kalip: sik kullanilan set+rol birlesimi tek secimle gelir, sonra
          tek tek duzenlenebilir. */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <select
          value=""
          onChange={(e) => {
            const p = presets.find((x) => x.id === e.target.value);
            if (p) onChange(p.refs ?? []);
          }}
          className="min-w-0 flex-1 rounded border border-gray-200 px-2 py-1 text-[11px]"
        >
          <option value="">Kalıptan uygula…</option>
          {presets.map((p) => <option key={p.id} value={p.id}>{p.name} ({(p.refs ?? []).length})</option>)}
        </select>
        <button
          onClick={savePreset}
          disabled={value.length === 0}
          className="rounded border border-indigo-300 px-2 py-1 text-[11px] font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-40"
        >
          Kalıp olarak kaydet
        </button>
      </div>

      <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px]">
        <span className={variationCount > 2 ? "font-medium text-red-600" : "text-gray-500"}>
          Varyasyon: {variationCount}/2 yuva
        </span>
        <span className={persoCount > 5 ? "font-medium text-red-600" : "text-gray-500"}>
          Kişiselleştirme: {persoCount}/5 alan
        </span>
      </div>

      {value.length === 0 ? (
        <p className="mb-2 rounded-lg border border-dashed border-gray-200 px-3 py-3 text-[11px] text-gray-400">
          Henüz seçenek eklenmedi. Kütüphanedeki setlerden ekleyin.
        </p>
      ) : (
        <div className="mb-2 space-y-1.5">
          {value.map((ref, i) => {
            const set = byId.get(ref.optionSetId);
            return (
              <div key={ref.optionSetId} className="flex items-center gap-2 rounded-lg border border-gray-200 px-2 py-1.5">
                <span className="min-w-0 flex-1 truncate text-xs text-gray-800">
                  {set?.name ?? "(silinmiş set)"}
                  {set && (
                    <span className="ml-1 text-[10px] text-gray-400">
                      {set.kind === "list" ? `${set.values.length} değer` : OPTION_KIND_LABEL[set.kind]}
                    </span>
                  )}
                </span>
                {set?.kind === "chart" ? (
                  <span className="rounded bg-emerald-50 px-1.5 py-1 text-[10px] text-emerald-700">
                    yalnızca görsel
                  </span>
                ) : (
                  <select
                    value={ref.role}
                    onChange={(e) => onChange(value.map((r, j) => (j === i ? { ...r, role: e.target.value as OptionRef["role"] } : r)))}
                    className="rounded border border-gray-200 px-1.5 py-1 text-[11px]"
                  >
                    <option value="variation">Varyasyon</option>
                    <option value="personalization">Kişiselleştirme</option>
                  </select>
                )}
                <label className={`flex items-center gap-1 text-[10px] text-gray-500 ${ref.role === "personalization" ? "" : "invisible"}`}>
                  <input
                    type="checkbox"
                    checked={ref.required !== false}
                    onChange={(e) => onChange(value.map((r, j) => (j === i ? { ...r, required: e.target.checked } : r)))}
                  />
                  zorunlu
                </label>
                <button
                  onClick={() => onChange(value.filter((_, j) => j !== i))}
                  className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {available.length > 0 ? (
        <select
          value=""
          onChange={(e) => e.target.value && add(e.target.value)}
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
        >
          <option value="">Seçenek seti ekle…</option>
          {available.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} ({s.kind === "list" ? `${s.values.length} değer` : OPTION_KIND_LABEL[s.kind]})
            </option>
          ))}
        </select>
      ) : (
        <p className="text-[11px] text-gray-400">
          {sets.length === 0
            ? "Kütüphanede set yok — Seçenek Kütüphanesi sekmesinden oluşturun."
            : "Tüm setler eklendi."}
        </p>
      )}

      {variationCount > 2 && (
        <p className="mt-1.5 text-[11px] text-red-600">
          Etsy'de 3 varyasyon yuvası var ve 1.'si ürünün fiyat eksenine ayrılı — fazlasını
          kişiselleştirmeye alın.
        </p>
      )}
      {persoCount > 5 && (
        <p className="mt-1.5 text-[11px] text-red-600">Etsy en fazla 5 kişiselleştirme alanı kabul ediyor.</p>
      )}
    </div>
  );
}
